import { ImgWithFallback } from "@/components/img-with-fallback";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useLocation, useSearch } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Edit, Copy, Archive, Plus, X, Megaphone, AlertTriangle,
  Brain, Target, Shield, Heart, Fingerprint, Loader2,
  MessageCircle, Lightbulb, ShieldAlert, TrendingUp,
  Quote, Ban, FileText, Crosshair, ChevronLeft, ChevronRight, Eye,
  Compass, Flame, Sprout,
  Users, UserPlus, Sparkles, Trash2, RefreshCw, ImageOff, Maximize2, Download,
  Image as ImageIcon, Check, Send, Star, ChevronDown, ChevronUp,
  Cake, MapPin, Globe2, Share2, Microscope, AlertCircle, CheckCircle2,
} from "lucide-react";
import { Link } from "wouter";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Persona, Campaign, PersonaCharacter } from "@shared/schema";
import { personaAvatarIsStalled } from "@shared/persona-avatar-lifecycle";
import { AD_IDENTITY_IMAGE_ROLES, resolveAdIdentityPackView } from "@/lib/ad-identity-pack-view";
import { apiRequest, queryClient, getQueryFn } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { optimizedImg } from "@/lib/img";
import { isPersonaCoverRegenerating, resolvePersonaCoverImage } from "@/lib/persona-cover";
import { InlineText, InlineTextarea, InlineSelect, InlineStringList } from "@/components/inline-edit";
import { isUnauthorizedError, redirectToLogin } from "@/lib/auth-utils";
import { PersonaEvidencePanel } from "@/components/persona-evidence-panel";
import { useCustomerEvidenceAccess } from "@/hooks/use-customer-evidence-access";

const PERSONA_STATUS_OPTIONS = [
  { value: "draft", label: "draft" },
  { value: "active", label: "active" },
];

const AWARENESS_STAGES = [
  { value: "unaware", label: "Unaware" },
  { value: "problem_aware", label: "Problem Aware" },
  { value: "solution_aware", label: "Solution Aware" },
  { value: "product_aware", label: "Product Aware" },
  { value: "most_aware", label: "Most Aware" },
];

const AWARENESS_STEP_COLORS: Record<string, string> = {
  unaware: "bg-gray-400",
  problem_aware: "bg-amber-500",
  solution_aware: "bg-blue-500",
  product_aware: "bg-violet-500",
  most_aware: "bg-emerald-500",
};


function AwarenessProgressBar({ currentStage, onSave }: { currentStage: string | null | undefined; onSave?: (v: string) => Promise<unknown> }) {
  const currentIndex = AWARENESS_STAGES.findIndex(s => s.value === currentStage);
  const activeColor = AWARENESS_STEP_COLORS[currentStage || ""] || "bg-gray-400";
  const currentLabel = AWARENESS_STAGES[currentIndex]?.label || "Unknown";

  return (
    <div className="flex max-w-full flex-wrap items-center gap-2" data-testid="indicator-awareness-progress">
      {onSave ? (
        <InlineSelect
          value={currentStage ?? null}
          options={AWARENESS_STAGES}
          onSave={onSave}
          placeholder="Set stage…"
          className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap"
          testId="persona-awareness-stage"
        />
      ) : (
        <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">
          {currentLabel}
        </span>
      )}
      <div className="flex items-center gap-1">
        {AWARENESS_STAGES.map((stage, i) => (
          <div
            key={stage.value}
            className={`h-2 w-5 rounded-full transition-all duration-300 ${
              i <= currentIndex ? activeColor : "bg-muted"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function CompletenessIndicator({ persona, onAdjust }: { persona: Persona; onAdjust?: () => void }) {
  const checks = [
    !!persona.name,
    !!persona.awarenessStage,
    !!persona.summary,
    !!persona.worldview,
    !!persona.primaryPain,
    !!persona.desiredOutcome,
    !!persona.deepFear,
    Array.isArray(persona.coreBeliefsJson) && persona.coreBeliefsJson.length > 0,
    Array.isArray(persona.objectionsJson) && persona.objectionsJson.length > 0,
    Array.isArray(persona.trustTriggersJson) && persona.trustTriggersJson.length > 0,
  ];
  const filled = checks.filter(Boolean).length;
  const total = checks.length;
  const pct = Math.round((filled / total) * 100);
  const filledSegments = Math.round((pct / 100) * 10);

  return (
    <div className="flex items-center gap-2.5 flex-wrap" data-testid="indicator-completeness">
      <span className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground/70 whitespace-nowrap">
        Progress
        {onAdjust && (
          <>
            {" / "}
            <button
              type="button"
              onClick={onAdjust}
              className="uppercase tracking-widest text-muted-foreground/70 hover:text-foreground transition-colors inline-flex items-center gap-1"
              data-testid="button-progress-adjust"
              title="Adjust persona details"
            >
              Adjust
              <Edit className="h-2.5 w-2.5" />
            </button>
          </>
        )}
      </span>
      <div className="flex items-center gap-[3px]">
        {Array.from({ length: 10 }).map((_, i) => (
          <div
            key={i}
            className={`h-1.5 w-4 rounded-full transition-all ${i < filledSegments ? "bg-emerald-500" : "bg-muted"}`}
          />
        ))}
      </div>
      <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">{pct}%</span>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, count, accent }: { icon: any; title: string; count?: number; accent?: string }) {
  const accentClass = accent ?? "bg-muted/60 text-muted-foreground/70";
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${accentClass}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-foreground/80">{title}</h2>
      {count !== undefined && (
        <span className="text-[10px] text-muted-foreground/70 bg-muted/70 rounded-full px-2 py-0.5 font-semibold tabular-nums">{count}</span>
      )}
    </div>
  );
}

function PsychField({
  label,
  content,
  icon: Icon,
  tint = "neutral",
  onSave,
}: {
  label: string;
  content: string | null | undefined;
  icon: any;
  tint?: "neutral" | "indigo" | "rose" | "emerald" | "amber";
  onSave?: (v: string) => Promise<unknown>;
}) {
  if (!content && !onSave) return null;
  const tints: Record<string, string> = {
    neutral: "border-border/50 bg-card",
    indigo: "border-violet-100 dark:border-violet-900/40 bg-violet-50/70 dark:bg-violet-950/20",
    rose: "border-pink-100 dark:border-pink-900/40 bg-pink-50/70 dark:bg-pink-950/20",
    emerald: "border-emerald-100 dark:border-emerald-900/40 bg-emerald-50/70 dark:bg-emerald-950/20",
    amber: "border-amber-100 dark:border-amber-900/40 bg-amber-50/70 dark:bg-amber-950/20",
  };
  const iconTints: Record<string, string> = {
    neutral: "bg-muted text-muted-foreground",
    indigo: "bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300",
    rose: "bg-pink-100 text-pink-600 dark:bg-pink-900/40 dark:text-pink-300",
    emerald: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300",
    amber: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  };
  const labelTints: Record<string, string> = {
    neutral: "text-foreground/60",
    indigo: "text-violet-700/80 dark:text-violet-300",
    rose: "text-pink-700/80 dark:text-pink-300",
    emerald: "text-emerald-700/80 dark:text-emerald-300",
    amber: "text-amber-700/80 dark:text-amber-300",
  };
  return (
    <div className={`rounded-xl border p-4 space-y-2.5 transition-all hover:shadow-sm ${tints[tint]}`}>
      <div className="flex items-center gap-2">
        <div className={`h-6 w-6 rounded-md flex items-center justify-center ${iconTints[tint]}`}>
          <Icon className="h-3 w-3" />
        </div>
        <p className={`text-[10px] font-semibold uppercase tracking-wider ${labelTints[tint]}`}>{label}</p>
      </div>
      {onSave ? (
        <InlineTextarea
          value={content ?? ""}
          onSave={onSave}
          placeholder={`Add ${label.toLowerCase()}…`}
          className="text-[13px] text-foreground/85 leading-relaxed"
          testId={`psych-${label.toLowerCase().replace(/\s+/g, "-")}`}
          rows={3}
        />
      ) : (
        <p className="text-[13px] text-foreground/85 leading-relaxed">{content}</p>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, tint }: { icon: any; label: string; value: number | string; tint: string }) {
  return (
    <div
      className="flex-1 min-w-[88px] flex flex-col items-center justify-center gap-1 px-2 py-3.5"
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-1.5">
        <Icon className={`h-3.5 w-3.5 ${tint}`} />
        <span className="text-[15px] font-bold tabular-nums text-foreground">{value}</span>
      </div>
      <span className="text-[11px] text-muted-foreground/70 lowercase leading-none">{label}</span>
    </div>
  );
}

interface PersonaResearchTopicView {
  id: string;
  topic: string;
  description?: string | null;
  knowledgeGap?: string | null;
  commercialUse?: string | null;
  status: string;
  findings?: string | null;
  sourcesJson?: unknown;
  error?: string | null;
}
export function CharactersSection({
  personaId,
  selectedCharacterId,
  onSelectCharacter,
}: {
  personaId: string;
  selectedCharacterId: string | null;
  onSelectCharacter: (id: string) => void;
}) {
  const { toast } = useToast();
  const [statusIdx, setStatusIdx] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [confirmRegenerateId, setConfirmRegenerateId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createMode, setCreateMode] = useState<"auto" | "describe">("auto");
  const [describeText, setDescribeText] = useState("");
  const [describeCount, setDescribeCount] = useState<1 | 3 | 5>(1);
  const [requestedCount, setRequestedCount] = useState<number>(5);
  const openCreateModal = (mode: "auto" | "describe" = "auto") => {
    setCreateMode(mode);
    setCreateOpen(true);
  };
  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const isStalled = (c: PersonaCharacter) => personaAvatarIsStalled(c);

  const { data: charactersRaw = [], isLoading } = useQuery<PersonaCharacter[]>({
    queryKey: ["/api/personas", personaId, "characters"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    refetchInterval: (query) => {
      const rows = (query.state.data as PersonaCharacter[] | undefined) || [];
      // Keep polling as long as ANY row is still pending/generating server-side,
      // even if the UI has already flipped that row into the "stalled" state.
      // This way, when Sim eventually completes, the card auto-flips to ready
      // instead of staying stuck on the Retry overlay until a manual refresh.
      const stillWorking = rows.some(
        (r) =>
          r.avatarStatus === "pending"
          || r.avatarStatus === "generating"
          || (r as any).referenceSheetStatus === "generating"
          || (r as any).bioStatus === "generating",
      );
      return stillWorking ? 3000 : false;
    },
  });

  const retryAvatarMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("POST", `/api/persona-characters/${id}/generate-avatar`);
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ["/api/personas", personaId, "characters"] });
      queryClient.setQueryData<PersonaCharacter[]>(
        ["/api/personas", personaId, "characters"],
        (rows = []) => rows.map((row) => row.id === id ? {
          ...row,
          avatarStatus: "pending",
          avatarError: null,
          avatarAttemptStartedAt: new Date(),
        } : row),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId, "characters"] });
      queryClient.invalidateQueries({ queryKey: [`/api/personas/${personaId}/characters`] });
      setConfirmRegenerateId(null);
      toast({ title: "Regenerating avatar…" });
    },
    onError: (e: Error) => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId, "characters"] });
      queryClient.invalidateQueries({ queryKey: [`/api/personas/${personaId}/characters`] });
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
  });

  const retryBioMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("POST", `/api/persona-characters/${id}/retry-bio`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId, "characters"] });
      toast({ title: "Re-reading bio from avatar…" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // Poll the persona to surface server-side generation state, so the inline
  // progress strip and disabled "Generating…" button re-hydrate after a
  // navigation away/back while the (multi-minute) sim workflow is still
  // running on the server.
  const { data: personaRow } = useQuery<Persona>({
    queryKey: ["/api/personas", personaId],
    queryFn: getQueryFn({ on401: "returnNull" }),
    refetchInterval: (query) => {
      const p = query.state.data as any;
      return p?.charactersGenerationStatus === "generating" ? 3000 : false;
    },
  });
  // Include every character — primary first so the grid order matches the
  // carousel order on the Avatar card above. Clicking a tile selects that
  // character on the carousel rather than opening a popup.
  const primaryCharId: string | null = (personaRow as any)?.primaryCharacterId ?? null;
  const characters: PersonaCharacter[] = primaryCharId
    ? [
        ...charactersRaw.filter((c) => c.id === primaryCharId),
        ...charactersRaw.filter((c) => c.id !== primaryCharId),
      ]
    : charactersRaw;

  const SERVER_STALE_MS = 15 * 60 * 1000;
  const serverGenerating = (() => {
    const p: any = personaRow;
    if (!p || p.charactersGenerationStatus !== "generating") return false;
    const ts = p.charactersGenerationStartedAt ? new Date(p.charactersGenerationStartedAt).getTime() : 0;
    return ts > 0 && Date.now() - ts < SERVER_STALE_MS;
  })();

  const generateMutation = useMutation({
    mutationFn: async (vars?: { userCharacterDirection?: string; characterCount?: 1 | 3 | 5 }) => {
      const body: Record<string, unknown> = {};
      if (vars?.userCharacterDirection) body.userCharacterDirection = vars.userCharacterDirection;
      if (vars?.characterCount) body.characterCount = vars.characterCount;
      const res = await apiRequest("POST", `/api/personas/${personaId}/generate-characters`, body);
      return res.json();
    },
    onMutate: (vars) => {
      setRequestedCount(vars?.characterCount ?? 5);
      // Optimistically reflect the server flag so other mounts see it right away.
      queryClient.setQueryData(["/api/personas", personaId], (prev: any) => prev
        ? { ...prev, charactersGenerationStatus: "generating", charactersGenerationStartedAt: new Date().toISOString(), charactersGenerationError: null }
        : prev);
    },
    onSuccess: (data: { characters: PersonaCharacter[] }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId, "characters"] });
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId] });
      toast({ title: `Generated ${data.characters?.length || 0} character${data.characters?.length === 1 ? "" : "s"}` });
    },
    onError: (e: Error) => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId] });
      toast({ title: "Generation failed", description: e.message, variant: "destructive" });
    },
  });

  const isGenerating = generateMutation.isPending || serverGenerating;

  const GENERATE_STATUS_MESSAGES = [
    "Reading the persona fingerprint…",
    "Drafting character variants…",
    "Casting faces and wardrobes…",
    "Rendering avatars…",
    "Finishing up…",
  ];

  useEffect(() => {
    if (!isGenerating) {
      setStatusIdx(0);
      return;
    }
    setStatusIdx(0);
    const id = setInterval(() => {
      setStatusIdx((i) => (i + 1) % GENERATE_STATUS_MESSAGES.length);
    }, 25000);
    return () => clearInterval(id);
  }, [isGenerating]);

  const serverGenerationError = (personaRow as any)?.charactersGenerationError as string | null | undefined;

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(
        ids.map((id) => apiRequest("DELETE", `/api/persona-characters/${id}`)),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed > 0) throw new Error(`${failed} character${failed === 1 ? "" : "s"} could not be deleted`);
      return ids.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId, "characters"] });
      clearSelection();
      setConfirmBulkDelete(false);
      toast({ title: `Removed ${count} character${count === 1 ? "" : "s"}` });
    },
    onError: (e: Error) => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId, "characters"] });
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
  });

  // Drop selections that no longer exist (e.g., after a single-delete from the preview).
  useEffect(() => {
    if (selectedIds.size === 0) return;
    const existing = new Set(characters.map((c) => c.id));
    let changed = false;
    const next = new Set<string>();
    selectedIds.forEach((id) => {
      if (existing.has(id)) next.add(id);
      else changed = true;
    });
    if (changed) setSelectedIds(next);
  }, [characters, selectedIds]);

  const selectionCount = selectedIds.size;

  return (
    <div className="mt-6 pt-5 border-t border-border/50" data-testid="card-characters">
      {isGenerating && (
        <div
          className="mb-4 rounded-lg border border-violet-200 dark:border-violet-900/50 bg-violet-50/60 dark:bg-violet-950/30 px-4 py-3 flex items-start gap-3"
          data-testid="strip-generating-characters"
        >
          <Loader2 className="h-4 w-4 mt-0.5 text-violet-600 dark:text-violet-300 animate-spin shrink-0" />
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-foreground" data-testid="text-generating-primary">
              Generating {requestedCount} new character{requestedCount === 1 ? "" : "s"}…
            </p>
            <p className="text-[12px] text-muted-foreground/80 mt-0.5" data-testid="text-generating-status">
              {GENERATE_STATUS_MESSAGES[statusIdx]}
            </p>
            <p className="text-[12px] text-muted-foreground/60 mt-1">
              This usually takes 4–5 minutes. You can leave this page — the characters will appear when they're ready.
            </p>
          </div>
        </div>
      )}

      {!isGenerating && (generateMutation.isError || serverGenerationError) && (
        <div
          className="mb-4 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 flex items-start gap-3"
          data-testid="strip-generate-characters-error"
        >
          <ImageOff className="h-4 w-4 mt-0.5 text-destructive shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-foreground">Generation failed</p>
            <p className="text-[12px] text-muted-foreground/80 mt-0.5 line-clamp-2">
              {(generateMutation.error as Error)?.message || serverGenerationError || "Something went wrong."}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[11px] shrink-0"
            onClick={() => generateMutation.mutate(undefined)}
            data-testid="button-retry-generate-characters"
          >
            <RefreshCw className="h-3 w-3 mr-1.5" />
            Try again
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="flex gap-2.5 overflow-hidden">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="w-[124px] sm:w-[140px] h-44 shrink-0 rounded-xl" />)}
        </div>
      ) : characters.length === 0 ? (
        <div className="py-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-muted/60 mb-3">
            <Users className="h-6 w-6 text-muted-foreground/40" />
          </div>
          <p className="text-sm text-muted-foreground/70 font-medium">No characters yet</p>
          <p className="text-[12px] text-muted-foreground/50 mt-1 max-w-sm mx-auto">
            Create persona-aligned characters automatically or guide the system with your own description.
          </p>
          <div className="mt-4 flex justify-center">
            <Button
              onClick={() => openCreateModal("auto")}
              disabled={isGenerating}
              data-testid="button-create-characters-empty"
            >
              {isGenerating ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating…</>
              ) : (
                <><UserPlus className="h-4 w-4 mr-2" />Create Characters</>
              )}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {(() => {
            const allSelected = characters.length > 0 && characters.every((c) => selectedIds.has(c.id));
            const toggleSelectAll = () => {
              if (allSelected) {
                clearSelection();
              } else {
                setSelectedIds(new Set(characters.map((c) => c.id)));
              }
            };
            return (
              <div className="mb-3 flex items-center justify-between gap-4">
                <button
                  type="button"
                  className="flex items-center gap-2 text-[12px] text-muted-foreground hover:text-foreground transition-colors py-1"
                  onClick={toggleSelectAll}
                  data-testid="button-select-all-characters"
                >
                  <span
                    className={`h-[18px] w-[18px] rounded-md border flex items-center justify-center transition-colors ${
                      allSelected
                        ? "bg-violet-600 border-violet-600 text-white"
                        : "border-border bg-background text-transparent"
                    }`}
                  >
                    <Check className="h-3 w-3" />
                  </span>
                  {allSelected ? "Deselect all" : "Select model"}
                </button>
                <div className="flex items-center">
                  <Button
                    size="sm"
                    className="h-8 px-4 rounded-r-none border-r border-violet-700/40"
                    onClick={() => openCreateModal("auto")}
                    disabled={isGenerating}
                    data-testid="button-create-characters"
                  >
                    {isGenerating ? (
                      <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Generating…</>
                    ) : (
                      <><UserPlus className="h-3.5 w-3.5 mr-1.5" />Add Character</>
                    )}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        className="h-8 px-2 rounded-l-none"
                        disabled={isGenerating}
                        aria-label="More character creation options"
                        data-testid="button-create-characters-menu"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem
                        onClick={() => openCreateModal("auto")}
                        data-testid="menu-create-mode-auto"
                      >
                        <UserPlus className="h-3.5 w-3.5 mr-2" />
                        Fully automatic
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => openCreateModal("describe")}
                        data-testid="menu-create-mode-describe"
                      >
                        <Sparkles className="h-3.5 w-3.5 mr-2" />
                        Describe what you want
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            );
          })()}
          {selectionCount > 0 && (
            <div
              className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-violet-300/70 dark:border-violet-700/60 bg-violet-50/80 dark:bg-violet-950/40 px-3 py-2"
              data-testid="bar-character-selection"
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="h-6 w-6 rounded-full bg-violet-600 text-white flex items-center justify-center shrink-0">
                  <Check className="h-3.5 w-3.5" />
                </div>
                <span className="text-[13px] font-medium text-foreground" data-testid="text-selection-count">
                  {selectionCount} selected
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground"
                  onClick={clearSelection}
                  data-testid="button-clear-selection"
                >
                  Clear
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-[11px] cursor-not-allowed opacity-60"
                  disabled
                  title="Coming soon"
                  data-testid="button-send-selected"
                >
                  <Send className="h-3 w-3 mr-1.5" />
                  Send to…
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-[11px] text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setConfirmBulkDelete(true)}
                  disabled={bulkDeleteMutation.isPending}
                  data-testid="button-delete-selected"
                >
                  {bulkDeleteMutation.isPending ? (
                    <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3 w-3 mr-1.5" />
                  )}
                  Delete
                </Button>
              </div>
            </div>
          )}
        <div className="flex gap-2.5 overflow-x-auto pb-2 -mx-1 px-1">
          {characters.map((c) => {
            const stalled = isStalled(c);
            const needsRetry = c.avatarStatus === "failed" || stalled;
            const isChecked = selectedIds.has(c.id);
            const isActive = selectedCharacterId === c.id;
            const isPrimary = primaryCharId === c.id;
            return (
              <div
                key={c.id}
                className={`relative w-[124px] sm:w-[140px] shrink-0 rounded-xl border bg-card transition-all flex flex-col group ${
                  isPrimary
                    ? "border-violet-500 ring-2 ring-violet-500/60 shadow-md shadow-violet-500/10"
                    : isChecked
                      ? "border-violet-500 ring-2 ring-violet-500/50 shadow-md shadow-violet-500/10 bg-violet-50/40 dark:bg-violet-950/30"
                      : isActive
                        ? "border-violet-400 ring-2 ring-violet-400/40 shadow-sm bg-violet-50/30 dark:bg-violet-950/20"
                        : "border-border/50 hover:bg-muted/40 hover:border-border"
                }`}
                data-testid={`character-card-${c.id}`}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSelected(c.id);
                  }}
                  className={`absolute top-2 left-2 z-10 h-6 w-6 rounded-md flex items-center justify-center transition-all ${
                    isChecked
                      ? "bg-violet-600 text-white shadow-sm opacity-100"
                      : "bg-background/90 backdrop-blur border border-border text-transparent opacity-0 group-hover:opacity-100 hover:border-violet-400 hover:text-violet-600"
                  }`}
                  aria-label={isChecked ? "Deselect character" : "Select character"}
                  data-testid={`checkbox-character-${c.id}`}
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
                {isPrimary && (
                  <>
                    <span
                      className="absolute top-1.5 right-1.5 z-10 inline-flex items-center gap-0.5 rounded-full bg-violet-600 text-white text-[8px] font-semibold uppercase tracking-wider px-1.5 py-0.5 shadow-sm"
                      data-testid={`badge-primary-character-${c.id}`}
                    >
                      <Star className="h-2 w-2 fill-current" />
                      Primary
                    </span>
                  </>
                )}
                {!isPrimary && (c as any).userCharacterDirection && (
                  <span
                    className="absolute top-1.5 right-1.5 z-10 inline-flex items-center gap-0.5 rounded-full bg-sky-600 text-white text-[8px] font-semibold uppercase tracking-wider px-1.5 py-0.5 shadow-sm"
                    title={(c as any).userCharacterDirection}
                    data-testid={`badge-described-character-${c.id}`}
                  >
                    <Sparkles className="h-2 w-2" />
                    Described
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (selectionCount > 0) toggleSelected(c.id);
                    else onSelectCharacter(c.id);
                  }}
                  className="text-left p-2 flex flex-col gap-1"
                  data-testid={`button-select-character-${c.id}`}
                >
                  <div className="relative aspect-square w-full rounded-lg overflow-hidden bg-muted/40 flex items-center justify-center mb-1">
                    {isPrimary && (
                      <span
                        className="absolute bottom-1.5 right-1.5 z-10 h-5 w-5 rounded-full bg-violet-600 text-white flex items-center justify-center shadow-sm"
                        aria-hidden="true"
                      >
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                    {c.avatarUrl ? (
                      <>
                        <img src={c.avatarUrl} alt={c.name} className="w-full h-full object-cover" data-testid={`img-character-${c.id}`} />
                        {(c.avatarStatus === "pending" || c.avatarStatus === "generating") && (
                          <div
                            className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/45 text-white"
                            data-testid={`overlay-regenerating-character-${c.id}`}
                          >
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="text-[9px] font-semibold uppercase tracking-wider">Regenerating</span>
                          </div>
                        )}
                        {needsRetry && (
                          <div
                            className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/50 text-white"
                            data-testid={`overlay-failed-character-${c.id}`}
                          >
                            <ImageOff className="h-4 w-4" />
                            <span className="text-[9px] font-semibold uppercase tracking-wider">
                              {c.avatarStatus === "failed" ? "Replacement failed" : "Regeneration stalled"}
                            </span>
                          </div>
                        )}
                      </>
                    ) : needsRetry ? (
                      <div className="flex flex-col items-center gap-1 text-muted-foreground/50 px-2 text-center">
                        <ImageOff className="h-4 w-4" />
                        <span className="text-[9px] uppercase tracking-wider">
                          {c.avatarStatus === "failed" ? "Failed" : "Stalled"}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-muted-foreground/50">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-[9px] uppercase tracking-wider">Rendering</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-[12px] font-semibold text-foreground/90 truncate leading-tight">{c.name}</span>
                  </div>
                  {(c as any).bioStatus === "generating" ? (
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground/70">
                      <Loader2 className="h-2.5 w-2.5 animate-spin" />
                      <span className="truncate">Reading bio…</span>
                    </div>
                  ) : (c as any).bioStatus === "failed" ? (
                    <div className="text-[10px] text-destructive line-clamp-1">Bio failed</div>
                  ) : (
                    <>
                      <div className="text-[10px] text-muted-foreground/70 line-clamp-1 leading-tight">
                        {[c.age, c.genderPresentation, c.ethnicityHeritage].filter(Boolean).join(" · ")}
                      </div>
                      {c.regionOrSetting && (
                        <div className="text-[10px] text-muted-foreground/60 line-clamp-1 leading-tight">{c.regionOrSetting}</div>
                      )}
                    </>
                  )}
                </button>
                {needsRetry && (
                  <div className="px-2 pb-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full h-6 text-[10px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        retryAvatarMutation.mutate(c.id);
                      }}
                      disabled={retryAvatarMutation.isPending}
                      data-testid={`button-retry-character-${c.id}`}
                    >
                      <RefreshCw className="h-3 w-3 mr-1.5" />
                      Retry avatar
                    </Button>
                  </div>
                )}
                {c.avatarStatus === "ready" && c.avatarUrl && (
                  <div className="px-2 pb-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full h-6 text-[10px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmRegenerateId(c.id);
                      }}
                      data-testid={`button-regenerate-character-${c.id}`}
                    >
                      <RefreshCw className="h-3 w-3 mr-1.5" />
                      Regenerate image
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}

      <AlertDialog open={confirmBulkDelete} onOpenChange={setConfirmBulkDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectionCount} character{selectionCount === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the selected character{selectionCount === 1 ? "" : "s"} and their avatar{selectionCount === 1 ? "" : "s"}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-bulk-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
              data-testid="button-confirm-bulk-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!confirmRegenerateId}
        onOpenChange={(open) => {
          if (!open && !retryAvatarMutation.isPending) setConfirmRegenerateId(null);
        }}
      >
        <AlertDialogContent data-testid="dialog-confirm-regenerate-avatar">
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate only this avatar image?</AlertDialogTitle>
            <AlertDialogDescription>
              This only generates a new avatar image. The character&apos;s name, bio, demographics,
              persona details, and other information will not change. The current image stays visible
              while regeneration runs, and already-submitted ads keep their original image snapshot.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={retryAvatarMutation.isPending}>Keep current image</AlertDialogCancel>
            <AlertDialogAction
              disabled={!confirmRegenerateId || retryAvatarMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (confirmRegenerateId) retryAvatarMutation.mutate(confirmRegenerateId);
              }}
              data-testid="button-confirm-regenerate-avatar"
            >
              {retryAvatarMutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {retryAvatarMutation.isPending ? "Starting…" : "Regenerate image only"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={createOpen} onOpenChange={(open) => {
        setCreateOpen(open);
        if (!open) {
          setDescribeText("");
          setDescribeCount(1);
          setCreateMode("auto");
        }
      }}>
        <DialogContent data-testid="dialog-create-characters">
          <DialogHeader>
            <DialogTitle>Create Characters</DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            <p className="text-[12px] font-medium text-foreground">Choose creation mode</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {([
                {
                  key: "auto" as const,
                  title: "Fully automatic",
                  desc: "Generate 5 visually distinct characters that fit this persona automatically.",
                },
                {
                  key: "describe" as const,
                  title: "Describe what you want",
                  desc: "Describe a character direction, then choose whether to generate 1, 3, or 5 aligned variations.",
                },
              ]).map((opt) => {
                const selected = createMode === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setCreateMode(opt.key)}
                    className={`text-left rounded-lg border p-3 transition-colors ${
                      selected
                        ? "border-violet-500 bg-violet-50/60 dark:bg-violet-950/30"
                        : "border-border bg-background hover:bg-muted/40"
                    }`}
                    data-testid={`button-create-mode-${opt.key}`}
                  >
                    <div className="flex items-start gap-2">
                      <div
                        className={`mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          selected ? "border-violet-600" : "border-muted-foreground/40"
                        }`}
                      >
                        {selected && <div className="h-2 w-2 rounded-full bg-violet-600" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-foreground">{opt.title}</p>
                        <p className="text-[12px] text-muted-foreground/80 mt-0.5 leading-relaxed">{opt.desc}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {createMode === "describe" && (
            <>
              <div className="space-y-2">
                <label className="text-[12px] font-medium text-foreground" htmlFor="describe-character-text">
                  Describe the character direction
                </label>
                <p className="text-[12px] text-muted-foreground">
                  PersonaOS will keep the result aligned with this persona's psychographic profile, demographic constraints, and behavioral identity.
                </p>
                <Textarea
                  id="describe-character-text"
                  value={describeText}
                  onChange={(e) => setDescribeText(e.target.value)}
                  placeholder="Example: brown-haired woman in her 40s with warm practical mom energy, understated style, and calm trustworthy presence"
                  rows={4}
                  maxLength={2000}
                  data-testid="textarea-describe-character"
                />
                <p className="text-[11px] text-muted-foreground/60 leading-relaxed" data-testid="text-describe-character-note">
                  Requests that strongly conflict with the persona may be safely reinterpreted or adjusted to remain consistent with the underlying audience profile.
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-[12px] font-medium text-foreground">How many characters?</p>
                <div className="inline-flex rounded-md border border-border overflow-hidden">
                  {([1, 3, 5] as const).map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setDescribeCount(n)}
                      className={`px-4 py-1.5 text-[13px] font-medium transition-colors ${
                        describeCount === n
                          ? "bg-violet-600 text-white"
                          : "bg-background text-muted-foreground hover:bg-muted"
                      }`}
                      data-testid={`button-describe-count-${n}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setCreateOpen(false)}
              data-testid="button-cancel-create-characters"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (createMode === "auto") {
                  generateMutation.mutate(undefined);
                } else {
                  const trimmed = describeText.trim();
                  if (!trimmed) return;
                  generateMutation.mutate({
                    userCharacterDirection: trimmed,
                    characterCount: describeCount,
                  });
                }
                setCreateOpen(false);
                setDescribeText("");
                setDescribeCount(1);
                setCreateMode("auto");
              }}
              disabled={
                isGenerating || (createMode === "describe" && !describeText.trim())
              }
              data-testid="button-submit-create-characters"
            >
              {createMode === "auto" ? (
                <><UserPlus className="h-4 w-4 mr-2" />Generate 5 Characters</>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  {describeCount === 1 ? "Generate Character" : "Generate Characters"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default function PersonaDetailPage() {
  const { brandId, personaId } = useParams<{ brandId: string; personaId: string }>();
  const evidenceAccess = useCustomerEvidenceAccess(brandId);
  const { user } = useAuth();
  const isMasterAdmin = user?.isMaster === true;
  const [, navigate] = useLocation();
  const search = useSearch();
  const { toast } = useToast();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const requestedCharacterId = useMemo(
    () => new URLSearchParams(search).get("characterId"),
    [search],
  );

  // Every inline editor funnels through this — PATCH only the edited
  // field(s). Derived shorts (deepFearShort, …) are server-managed: never
  // send them; the server clears + regenerates when a full field changes.
  const saveMutation = useMutation({
    mutationFn: async (patch: Record<string, any>) => {
      const res = await apiRequest("PATCH", `/api/personas/${personaId}`, patch);
      return res.json();
    },
    onSuccess: (updated: Persona) => {
      queryClient.setQueryData(["/api/personas", personaId], (previous: Persona | undefined) => ({
        ...updated,
        ...(evidenceAccess.enabled && previous?.evidenceLink ? { evidenceLink: previous.evidenceLink } : {}),
      }));
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/personas`] });
    },
    onError: (e: Error) => {
      if (isUnauthorizedError(e)) {
        redirectToLogin(toast);
        return;
      }
      toast({
        title: "Couldn’t save yet",
        description: "Your edit is still here. Check your connection and try saving again.",
        variant: "destructive",
      });
    },
  });
  const save = (patch: Record<string, any>) => saveMutation.mutateAsync(patch);
  const listOf = (v: unknown): string[] => (Array.isArray(v) ? v.filter((s): s is string => typeof s === "string") : []);

  const { data: persona, isLoading } = useQuery<Persona>({
    queryKey: ["/api/personas", personaId],
    queryFn: getQueryFn({ on401: "returnNull" }),
    refetchInterval: (query) => {
      const p = query.state.data as any;
      // buildStatus === "building" keeps polling until the aggregate build
      // (5 characters + research) resolves, not just until the profile lands.
      if (p && (!p.imageUrl || !p.fingerprint || p.status === "generating" || p.buildStatus === "building" || p.referenceSheetStatus === "generating" || p.adIdentityPackStatus === "generating" || p.profileEnrichmentStatus === "generating")) return 3000;
      return false;
    },
  });

  // Shared characters query (also used inside CharactersSection — react-query
  // dedupes the network call). We need it at page level so the primary-avatar
  // fact-sheet Dialog can surface the full bio of the linked character row.
  const { data: pageCharacters = [] } = useQuery<PersonaCharacter[]>({
    queryKey: ["/api/personas", personaId, "characters"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!personaId,
    refetchInterval: (query) => {
      // Keep the primary-character bio fresh while the vision workflow is
      // running, so the inline "Reading bio from avatar…" placeholder in
      // the Avatar card auto-flips to the real fields once Sim completes.
      const rows = (query.state.data as PersonaCharacter[] | undefined) || [];
      const stillWorking = rows.some(
        (r) =>
          (r as any).bioStatus === "generating"
          || r.avatarStatus === "pending"
          || r.avatarStatus === "generating"
          || (r as any).referenceSheetStatus === "generating"
          || (r as any).adIdentityPackStatus === "generating",
      );
      return stillWorking ? 3000 : false;
    },
  });
  const primaryCharacter: PersonaCharacter | null =
    (persona as any)?.primaryCharacterId
      ? pageCharacters.find((c) => c.id === (persona as any).primaryCharacterId) || null
      : null;
  const coverRegenerating = isPersonaCoverRegenerating(persona);
  // Always feed the primary-avatar fact-sheet Dialog a "display" object so the
  // attributes card is visible even for the original free-floating primary
  // (which has no character row to draw from). Missing fields render as null /
  // em-dashes — the card structure is the same as a promoted character's card.
  const primaryDisplay = {
    age: primaryCharacter?.age ?? null,
    genderPresentation: primaryCharacter?.genderPresentation ?? null,
    ethnicityHeritage: primaryCharacter?.ethnicityHeritage ?? null,
    regionOrSetting: primaryCharacter?.regionOrSetting ?? null,
    imagePromptSummary: primaryCharacter?.imagePromptSummary ?? null,
    physicalLook: (primaryCharacter?.physicalLook as Record<string, any> | null) ?? null,
    styleAndWardrobe: (primaryCharacter?.styleAndWardrobe as Record<string, any> | null) ?? null,
    cameraPresence: (primaryCharacter?.cameraPresence as Record<string, any> | null) ?? null,
    visualDistinction: (primaryCharacter?.visualDistinction as Record<string, any> | null) ?? null,
  };
  // The header photo is the persona-level cover image, which can be regenerated
  // independently of character avatars. Other primary-character facts continue
  // to come from the linked character row when one exists.
  const primaryView = {
    imageUrl: coverRegenerating
      ? null
      : resolvePersonaCoverImage((persona as any)?.imageUrl, primaryCharacter?.avatarUrl),
    name: (primaryCharacter?.name ?? (persona as any)?.avatarName ?? null) as string | null,
    referenceSheetUrl: ((primaryCharacter as any)?.referenceSheetUrl ?? (persona as any)?.referenceSheetUrl ?? null) as string | null,
    referenceSheetStatus: ((primaryCharacter as any)?.referenceSheetStatus ?? (persona as any)?.referenceSheetStatus ?? null) as string | null,
    referenceSheetError: ((primaryCharacter as any)?.referenceSheetError ?? (persona as any)?.referenceSheetError ?? null) as string | null,
  };

  const [avatarNameDraft, setAvatarNameDraft] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [adIdentityPackOpen, setAdIdentityPackOpen] = useState(false);
  const [adIdentityPackCharacterId, setAdIdentityPackCharacterId] = useState<string | null>(null);
  const [deleteAdIdentityPackOpen, setDeleteAdIdentityPackOpen] = useState(false);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [selectedCharIdx, setSelectedCharIdx] = useState(0);
  const primaryBioStatus = (primaryCharacter as any)?.bioStatus as string | null | undefined;
  const primaryBioError = (primaryCharacter as any)?.bioError as string | null | undefined;

  // Carousel data: primary character first, then any siblings. Falls back to
  // an empty list when the persona has no character rows yet, in which case
  // the Avatar card renders the persona-level denorm avatar via primaryView.
  const orderedCharacters: PersonaCharacter[] = primaryCharacter
    ? [primaryCharacter, ...pageCharacters.filter((c) => c.id !== primaryCharacter.id)]
    : pageCharacters;
  const totalChars = orderedCharacters.length;
  const safeIdx = totalChars > 0 ? ((selectedCharIdx % totalChars) + totalChars) % totalChars : 0;
  const currentChar: PersonaCharacter | null = orderedCharacters[safeIdx] ?? null;
  const { data: videoReferenceSheetData } = useQuery<{
    snapshot: {
      runId: string;
      url: string;
      status: string;
      createdAt: string | null;
    } | null;
  }>({
    queryKey: [
      "/api/personas",
      personaId,
      "characters",
      currentChar?.id,
      "video-reference-sheet-snapshot",
    ],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isMasterAdmin && !!personaId && !!currentChar?.id,
  });
  const videoReferenceSheetSnapshot = videoReferenceSheetData?.snapshot ?? null;
  const [videoReferenceSheetOpen, setVideoReferenceSheetOpen] = useState(false);
  const [deleteVideoReferenceSheetOpen, setDeleteVideoReferenceSheetOpen] = useState(false);
  useEffect(() => {
    setVideoReferenceSheetOpen(false);
    setDeleteVideoReferenceSheetOpen(false);
    setAdIdentityPackOpen(false);
    setDeleteAdIdentityPackOpen(false);
  }, [currentChar?.id]);
  const focusedCharacterAppliedRef = useRef(false);
  useEffect(() => {
    if (focusedCharacterAppliedRef.current || !requestedCharacterId || orderedCharacters.length === 0) {
      return;
    }
    const requestedIndex = orderedCharacters.findIndex((character) => character.id === requestedCharacterId);
    focusedCharacterAppliedRef.current = true;
    if (requestedIndex >= 0) {
      setSelectedCharIdx(requestedIndex);
      requestAnimationFrame(() => {
        document.querySelector(`[data-testid="character-card-${requestedCharacterId}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  }, [orderedCharacters, requestedCharacterId]);
  const isViewingPrimary =
    !!currentChar && !!primaryCharacter && currentChar.id === primaryCharacter.id;

  const currentView = currentChar
    ? {
        imageUrl: (currentChar.avatarUrl ?? null) as string | null,
        name: (currentChar.name ?? null) as string | null,
        referenceSheetUrl: ((currentChar as any).referenceSheetUrl ?? null) as string | null,
        referenceSheetStatus: ((currentChar as any).referenceSheetStatus ?? null) as string | null,
        referenceSheetError: ((currentChar as any).referenceSheetError ?? null) as string | null,
        adIdentityPackUrls: resolveAdIdentityPackView(currentChar).images,
        adIdentityPackStatus: ((currentChar as any).adIdentityPackStatus ?? null) as string | null,
        adIdentityPackError: ((currentChar as any).adIdentityPackError ?? null) as string | null,
        adIdentityPackStartedAt: ((currentChar as any).adIdentityPackStartedAt ?? null) as string | null,
      }
    : {
        ...primaryView,
        adIdentityPackUrls: [],
        adIdentityPackStatus: null,
        adIdentityPackError: null,
        adIdentityPackStartedAt: null,
      };
  const adIdentityPackView = resolveAdIdentityPackView(currentChar);
  const adIdentityPackIsActive = adIdentityPackView.active;
  const adIdentityPackIsInterrupted =
    currentView.adIdentityPackStatus === "generating" && !adIdentityPackIsActive;
  const adIdentityPackReady = adIdentityPackView.ready;
  const adIdentityPackState = adIdentityPackView.state;
  const openAdIdentityPack = () => {
    if (!currentChar) return;
    setAdIdentityPackCharacterId(currentChar.id);
    setAdIdentityPackOpen(true);
  };
  const currentDisplay = currentChar
    ? {
        age: currentChar.age ?? null,
        genderPresentation: currentChar.genderPresentation ?? null,
        ethnicityHeritage: currentChar.ethnicityHeritage ?? null,
        regionOrSetting: currentChar.regionOrSetting ?? null,
        imagePromptSummary: currentChar.imagePromptSummary ?? null,
        physicalLook: (currentChar.physicalLook as Record<string, any> | null) ?? null,
        styleAndWardrobe: (currentChar.styleAndWardrobe as Record<string, any> | null) ?? null,
        cameraPresence: (currentChar.cameraPresence as Record<string, any> | null) ?? null,
        visualDistinction: (currentChar.visualDistinction as Record<string, any> | null) ?? null,
      }
    : primaryDisplay;
  const currentBioStatus = currentChar
    ? ((currentChar as any).bioStatus as string | null | undefined)
    : primaryBioStatus;
  const currentBioError = currentChar
    ? ((currentChar as any).bioError as string | null | undefined)
    : primaryBioError;
  const currentLabel = isViewingPrimary
    ? "Primary"
    : currentChar
      ? `Character ${safeIdx + 1} of ${totalChars}`
      : "";
  const goPrevChar = () =>
    setSelectedCharIdx((i) => (totalChars > 0 ? (i - 1 + totalChars) % totalChars : 0));
  const goNextChar = () =>
    setSelectedCharIdx((i) => (totalChars > 0 ? (i + 1) % totalChars : 0));

  const retryBioMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("POST", `/api/persona-characters/${id}/retry-bio`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId, "characters"] });
      toast({ title: "Re-reading bio from avatar…" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const saveAvatarNameMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("PATCH", `/api/personas/${personaId}`, { avatarName: name || null });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId] });
      setAvatarNameDraft(null);
      toast({ title: "Name saved" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const setPrimaryMutation = useMutation({
    mutationFn: async (charId: string) => {
      await apiRequest("PATCH", `/api/personas/${personaId}`, { primaryCharacterId: charId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId] });
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId, "characters"] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/personas`] });
      toast({ title: "Set as primary character" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const [confirmDeleteCharId, setConfirmDeleteCharId] = useState<string | null>(null);
  const deleteCharMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/persona-characters/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId, "characters"] });
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId] });
      // Snap carousel back to the first slot — the deleted slot is gone and
      // we don't want a dangling index lingering past the array length.
      setSelectedCharIdx(0);
      toast({ title: "Character removed" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const avatarCardRef = useRef<HTMLDivElement>(null);
  const handleSelectCharacterFromGrid = (charId: string) => {
    const idx = orderedCharacters.findIndex((c) => c.id === charId);
    if (idx >= 0) setSelectedCharIdx(idx);
    // Pull the carousel into view so the selection is obviously reflected up
    // top — without this the user can click a tile and not realize the
    // carousel jumped on a long page.
    requestAnimationFrame(() => {
      avatarCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  type BioDraft = {
    name: string;
    age: string;
    genderPresentation: string;
    ethnicityHeritage: string;
    regionOrSetting: string;
    imagePromptSummary: string;
    physicalLook: Array<{ k: string; v: string }>;
    styleAndWardrobe: Array<{ k: string; v: string }>;
    cameraPresence: Array<{ k: string; v: string }>;
    visualDistinction: Array<{ k: string; v: string }>;
  };
  const [bioEditingId, setBioEditingId] = useState<string | null>(null);
  const [bioDraft, setBioDraft] = useState<BioDraft | null>(null);
  const objToRows = (o: Record<string, any> | null | undefined): Array<{ k: string; v: string }> =>
    o && typeof o === "object"
      ? Object.entries(o).map(([k, v]) => ({ k, v: Array.isArray(v) ? v.join(", ") : String(v ?? "") }))
      : [];
  const rowsToObj = (rows: Array<{ k: string; v: string }>): Record<string, any> => {
    const out: Record<string, any> = {};
    for (const { k, v } of rows) {
      const key = k.trim();
      if (!key) continue;
      out[key] = v;
    }
    return out;
  };
  const startBioEdit = () => {
    if (!currentChar) return;
    setBioEditingId(currentChar.id);
    setBioDraft({
      name: currentChar.name ?? "",
      age: currentChar.age != null ? String(currentChar.age) : "",
      genderPresentation: currentChar.genderPresentation ?? "",
      ethnicityHeritage: currentChar.ethnicityHeritage ?? "",
      regionOrSetting: currentChar.regionOrSetting ?? "",
      imagePromptSummary: currentChar.imagePromptSummary ?? "",
      physicalLook: objToRows(currentChar.physicalLook as Record<string, any> | null),
      styleAndWardrobe: objToRows(currentChar.styleAndWardrobe as Record<string, any> | null),
      cameraPresence: objToRows(currentChar.cameraPresence as Record<string, any> | null),
      visualDistinction: objToRows(currentChar.visualDistinction as Record<string, any> | null),
    });
    setBioExpanded(true);
  };
  const cancelBioEdit = () => { setBioEditingId(null); setBioDraft(null); };
  const saveBioMutation = useMutation({
    mutationFn: async () => {
      if (!bioEditingId || !bioDraft) throw new Error("No draft");
      const name = bioDraft.name.trim();
      if (!name) throw new Error("Name cannot be empty");
      const ageNum = bioDraft.age.trim() === "" ? null : parseInt(bioDraft.age, 10);
      if (bioDraft.age.trim() !== "" && !Number.isFinite(ageNum)) throw new Error("Age must be a number");
      await apiRequest("PATCH", `/api/persona-characters/${bioEditingId}`, {
        name,
        age: ageNum,
        genderPresentation: bioDraft.genderPresentation.trim() || null,
        ethnicityHeritage: bioDraft.ethnicityHeritage.trim() || null,
        regionOrSetting: bioDraft.regionOrSetting.trim() || null,
        imagePromptSummary: bioDraft.imagePromptSummary.trim() || null,
        physicalLook: rowsToObj(bioDraft.physicalLook),
        styleAndWardrobe: rowsToObj(bioDraft.styleAndWardrobe),
        cameraPresence: rowsToObj(bioDraft.cameraPresence),
        visualDistinction: rowsToObj(bioDraft.visualDistinction),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId] });
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId, "characters"] });
      toast({ title: "Bio saved" });
      cancelBioEdit();
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const generateSheetMutation = useMutation({
    mutationFn: async (charId: string | null) => {
      // For any linked character row (primary or sibling visible in the
      // carousel) generate the reference sheet on THAT character so image +
      // refsheet stay inseparable on the same record. Fall back to the
      // persona-level endpoint only for the original free-floating primary
      // that has no character row, which writes to persona denorm fields.
      if (charId) {
        await apiRequest("POST", `/api/persona-characters/${charId}/generate-reference-sheet`);
      } else {
        await apiRequest("POST", `/api/personas/${personaId}/generate-reference-sheet`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId] });
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId, "characters"] });
      toast({ title: "Generating character reference sheet…" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
  const deleteVideoReferenceSheetMutation = useMutation({
    mutationFn: async (characterId: string) => {
      await apiRequest("DELETE", `/api/persona-characters/${characterId}/reference-sheet`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId] });
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId, "characters"] });
      setVideoReferenceSheetOpen(false);
      setDeleteVideoReferenceSheetOpen(false);
      toast({
        title: "Cached sheet deleted",
        description: "The next Video Reference run will generate a new identity sheet.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Couldn’t delete sheet", description: error.message, variant: "destructive" });
    },
  });
  const generateAdIdentityPackMutation = useMutation({
    mutationFn: async (characterId: string) => {
      await apiRequest("POST", `/api/persona-characters/${characterId}/generate-ad-identity-pack`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId, "characters"] });
      toast({ title: "Generating Ads Express identity pack…" });
    },
    onError: (error: Error) => {
      toast({ title: "Couldn’t generate identity pack", description: error.message, variant: "destructive" });
    },
  });
  const deleteAdIdentityPackMutation = useMutation({
    mutationFn: async (characterId: string) => {
      await apiRequest("DELETE", `/api/persona-characters/${characterId}/ad-identity-pack`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId, "characters"] });
      setDeleteAdIdentityPackOpen(false);
      toast({ title: "Ads Express identity pack deleted" });
    },
    onError: (error: Error) => {
      toast({ title: "Couldn’t delete identity pack", description: error.message, variant: "destructive" });
    },
  });

  const { data: campaigns } = useQuery<Campaign[]>({
    queryKey: [`/api/brands/${brandId}/campaigns`],
    enabled: !!brandId,
  });

  const linkedCampaigns = campaigns?.filter(c => c.personaId === personaId) || [];

  const duplicateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/personas/${personaId}/duplicate`);
      return res.json();
    },
    onSuccess: (data: Persona) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/personas`] });
      toast({ title: "Persona duplicated" });
      navigate(`/brands/${brandId}/personas/${data.id}`);
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const archiveMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/personas/${personaId}/archive`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/personas`] });
      toast({ title: "Persona archived" });
      navigate(`/brands/${brandId}/personas`);
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const enrichProfileMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/personas/${personaId}/enrich-profile`);
    },
    onSuccess: () => {
      // Flip the cached row into "generating" immediately so the polling
      // refetchInterval kicks in without waiting for the next fetch.
      queryClient.setQueryData(["/api/personas", personaId], (prev: any) =>
        prev ? { ...prev, profileEnrichmentStatus: "generating", profileEnrichmentError: null } : prev,
      );
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId] });
      toast({ title: "Completing profile…", description: "Filling in the psychology fields. This takes a few minutes." });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const fingerprintMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/personas/${personaId}/generate-fingerprint`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/personas', personaId] });
      toast({ title: "Fingerprint generated" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const regenerateCoverMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/personas/${personaId}/generate-image`);
      return res.json() as Promise<{ imageUrl: string }>;
    },
    onMutate: async () => {
      const detailKey = ["/api/personas", personaId] as const;
      const listKey = [`/api/brands/${brandId}/personas`] as const;
      await Promise.all([
        queryClient.cancelQueries({ queryKey: detailKey }),
        queryClient.cancelQueries({ queryKey: listKey }),
      ]);
      const previousDetail = queryClient.getQueryData<Persona>(detailKey);
      const previousList = queryClient.getQueryData<Persona[]>(listKey);
      queryClient.setQueryData<Persona>(detailKey, (prev) =>
        prev ? { ...prev, imageUrl: null, coverRegenerationStatus: "generating" } : prev,
      );
      queryClient.setQueryData<Persona[]>(listKey, (prev) =>
        prev?.map((item) => item.id === personaId
          ? { ...item, imageUrl: null, coverRegenerationStatus: "generating" }
          : item),
      );
      return { previousDetail, previousList };
    },
    onSuccess: ({ imageUrl }) => {
      queryClient.setQueryData(["/api/personas", personaId], (prev: Persona | undefined) =>
        prev ? { ...prev, imageUrl, coverRegenerationStatus: "ready" } : prev,
      );
      queryClient.setQueryData<Persona[]>([`/api/brands/${brandId}/personas`], (prev) =>
        prev?.map((item) => item.id === personaId
          ? { ...item, imageUrl, coverRegenerationStatus: "ready" }
          : item),
      );
      toast({ title: "Cover photo regenerated" });
    },
    onError: (e: Error, _variables, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(["/api/personas", personaId], context.previousDetail);
      }
      if (context?.previousList) {
        queryClient.setQueryData([`/api/brands/${brandId}/personas`], context.previousList);
      }
      toast({
        title: "Couldn't regenerate cover photo",
        description: e.message,
        variant: "destructive",
      });
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId] }),
        queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/personas`] }),
      ]);
    },
  });

  if (isLoading) {
    return (
      <div className="p-8 max-w-5xl mx-auto space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-24 w-full rounded-2xl" />
        <div className="grid grid-cols-2 gap-5"><Skeleton className="h-44 rounded-2xl" /><Skeleton className="h-44 rounded-2xl" /></div>
        <Skeleton className="h-52 w-full rounded-2xl" />
      </div>
    );
  }
  if (!persona) return <div className="p-8 text-muted-foreground">Persona not found</div>;
  const evidenceLink = (persona as any).evidenceLink;

  if (persona.status === "generating") {
    return (
      <div className="p-8 max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Link href={`/brands/${brandId}/personas`}>
            <Button variant="ghost" size="icon" className="shrink-0 rounded-full" data-testid="button-back-personas">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-bold tracking-tight">{persona.name}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Generating persona profile...</p>
          </div>
        </div>
        <div className="rounded-xl border border-primary/20 bg-primary/[0.02] p-8 flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
          <div className="text-center space-y-1">
            <p className="font-medium">Generating persona profile</p>
            <p className="text-sm text-muted-foreground">This usually takes 2-5 minutes. You can navigate away — you'll be notified when it's ready.</p>
          </div>
          <p className="text-xs text-muted-foreground/60 italic mt-2">"{persona.summary}"</p>
        </div>
      </div>
    );
  }

  const enrichmentStatus = (persona as any).profileEnrichmentStatus as string | null | undefined;
  // A "slim" persona (e.g. saved from the Persona Ideator) is missing all of
  // the deep psychology fields the full-profile Sim workflow fills in.
  const isSlimProfile =
    !persona.primaryPain &&
    !persona.desiredOutcome &&
    !persona.deepFear &&
    !(Array.isArray(persona.coreBeliefsJson) && persona.coreBeliefsJson.length > 0) &&
    !(Array.isArray(persona.languagePatternsJson) && persona.languagePatternsJson.length > 0);

  let sectionDelay = 0;
  const nextDelay = () => {
    sectionDelay++;
    return `fade-in-up-delay-${Math.min(sectionDelay, 7)}`;
  };

  const buildProgress = (persona as any).buildStatus === "building"
    ? ((persona as any).buildProgress as {
        profile: string;
        image?: string;
        enrichment: string;
        characters: { state: string; created: number; target: number };
        research: { state: string; done: number; total: number };
      } | undefined)
    : undefined;

  return (
    <div className="min-h-full bg-slate-50/80 dark:bg-transparent">
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4">

      {buildProgress && (
        <div className="fade-in-up rounded-xl border border-primary/20 bg-primary/[0.03] px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="banner-persona-building">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground/80">
            <Loader2 className="h-4 w-4 text-primary animate-spin" />
            Finishing this persona…
          </div>
          {[
            ...(buildProgress.image ? [{ key: "image", label: "Portrait", state: buildProgress.image }] : []),
            { key: "characters", label: `Characters ${buildProgress.characters.created}/${buildProgress.characters.target}`, state: buildProgress.characters.state },
            { key: "research", label: buildProgress.research.total > 0 ? `Research ${buildProgress.research.done}/${buildProgress.research.total}` : "Research", state: buildProgress.research.state },
          ].map((step) =>
            step.state === "none" ? null : (
              <div key={step.key} className="flex items-center gap-1.5 text-xs" data-testid={`banner-build-step-${step.key}`}>
                {step.state === "complete" ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                ) : step.state === "failed" ? (
                  <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                ) : step.state === "working" ? (
                  <Loader2 className="h-3.5 w-3.5 text-primary/60 animate-spin" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/30" />
                )}
                <span className={step.state === "failed" ? "text-destructive/80" : "text-muted-foreground"}>
                  {step.label}
                  {step.state === "failed" ? " — failed (see section below to retry)" : ""}
                </span>
              </div>
            ),
          )}
        </div>
      )}

      <div className="fade-in-up rounded-2xl border border-border/60 bg-card shadow-sm p-4 md:p-5" data-testid="card-persona-header">
        <div className="flex items-center gap-3 md:gap-4 flex-wrap">
          <Link href={`/brands/${brandId}/personas`}>
            <Button variant="ghost" size="icon" className="shrink-0 rounded-full border border-border/50" data-testid="button-back-personas">
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </Link>
          {primaryView.imageUrl ? (
            <button onClick={() => { setSelectedCharIdx(0); setAvatarOpen(true); }} className="w-14 h-14 rounded-full overflow-hidden ring-2 ring-violet-500 ring-offset-2 ring-offset-card flex-shrink-0 cursor-pointer hover:ring-violet-400 transition-all" data-testid="button-view-avatar">
              <img src={optimizedImg(primaryView.imageUrl, 112)} alt={primaryView.name || persona.name} className="w-full h-full object-cover" />
            </button>
          ) : (
            <div className="w-14 h-14 rounded-full ring-2 ring-violet-200 dark:ring-violet-900/50 ring-offset-2 ring-offset-card flex-shrink-0 flex items-center justify-center bg-muted/30">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/50" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <InlineText
                value={persona.name}
                onSave={(v) => save({ name: v })}
                placeholder="Add name…"
                className="text-xl md:text-2xl font-bold tracking-tight"
                testId="persona-name"
              />
              <span
                className={`inline-flex items-center text-[10px] rounded-full px-2.5 py-0.5 border font-medium shrink-0 ${persona.status === "active" ? "border-emerald-300 text-emerald-600 bg-emerald-50 dark:border-emerald-700 dark:text-emerald-400 dark:bg-emerald-950/30" : "border-border text-muted-foreground bg-muted/40"}`}
              >
                <InlineSelect
                  value={persona.status}
                  options={PERSONA_STATUS_OPTIONS}
                  onSave={(v) => save({ status: v })}
                  testId="persona-status"
                  className="text-[10px] font-medium"
                  disabled={persona.status !== "active" && persona.status !== "draft"}
                />
              </span>
            </div>
            <div className="flex items-center gap-4 flex-wrap mt-2">
              <CompletenessIndicator persona={persona} />
              <div className="h-3.5 w-px bg-border/60 hidden sm:block" />
              <AwarenessProgressBar currentStage={persona.awarenessStage} onSave={(v) => save({ awarenessStage: v })} />
            </div>
          </div>
          <div className="flex w-full min-w-0 flex-wrap items-center gap-1.5 xl:w-auto">
            {saveMutation.isPending && (
              <span className="flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="status-persona-saving">
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving…
              </span>
            )}
            <Button
              variant="ghost"
              size="icon"
              className={`h-8 w-8 rounded-full ${persona.fingerprint ? "text-violet-500" : ""}`}
              onClick={() => fingerprintMutation.mutate()}
              disabled={fingerprintMutation.isPending}
              title={persona.fingerprint ? "Regenerate fingerprint" : "Generate fingerprint"}
              data-testid="button-generate-fingerprint"
            >
              {fingerprintMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Fingerprint className="h-3.5 w-3.5" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-[12px]"
              onClick={() => regenerateCoverMutation.mutate()}
              disabled={regenerateCoverMutation.isPending || coverRegenerating}
              data-testid="button-regenerate-persona-cover"
            >
              {regenerateCoverMutation.isPending || coverRegenerating ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              )}
              {regenerateCoverMutation.isPending || coverRegenerating ? "Regenerating…" : "Regenerate cover photo"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-[12px] cursor-not-allowed opacity-60"
              disabled
              title="Coming soon"
              data-testid="button-share-persona"
            >
              <Share2 className="h-3.5 w-3.5 mr-1.5" />
              Share
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-[12px]" onClick={() => duplicateMutation.mutate()} disabled={duplicateMutation.isPending} data-testid="button-duplicate-persona">
              <Copy className="h-3.5 w-3.5 mr-1.5" />
              Duplicate
            </Button>
            <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-[12px] text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive" disabled={archiveMutation.isPending} data-testid="button-archive-persona">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="dialog-archive-persona">
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    Archive this persona?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    This persona will be removed from your dashboard and excluded from new campaigns. Existing campaigns referencing it will continue to work, but you will not be able to recover this persona on your own — an administrator will need to restore it for you.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="button-archive-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => archiveMutation.mutate()}
                    data-testid="button-archive-confirm"
                  >
                    {archiveMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Archive Persona"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      <PersonaEvidencePanel
        brandId={brandId}
        personaId={persona.id}
        evidenceLink={evidenceLink}
        profileAvailable={typeof persona.summary === "string" && persona.summary.trim().length >= 20}
      />

      <div
        className={`rounded-2xl border border-border/60 bg-card shadow-sm flex flex-wrap items-stretch divide-x divide-border/40 overflow-hidden ${nextDelay()}`}
        data-testid="card-persona-stats"
      >
        <StatTile icon={Lightbulb} tint="text-amber-500" label="beliefs" value={(persona.coreBeliefsJson as any[] | null)?.length ?? 0} />
        <StatTile icon={ShieldAlert} tint="text-rose-500" label="rejections" value={(persona.objectionsJson as any[] | null)?.length ?? 0} />
        <StatTile icon={Shield} tint="text-emerald-500" label="triggers" value={(persona.trustTriggersJson as any[] | null)?.length ?? 0} />
        <StatTile icon={TrendingUp} tint="text-violet-500" label="drivers" value={(persona.purchaseDriversJson as any[] | null)?.length ?? 0} />
        <StatTile icon={Heart} tint="text-pink-500" label="hooks" value={(persona.emotionalHooksJson as any[] | null)?.length ?? 0} />
        <StatTile icon={MessageCircle} tint="text-sky-500" label="language" value={(persona.languagePatternsJson as any[] | null)?.length ?? 0} />
        <StatTile icon={Quote} tint="text-indigo-500" label="voice" value={(persona.customerVoiceQuotesJson as any[] | null)?.length ?? 0} />
        <StatTile icon={Megaphone} tint="text-teal-500" label="campaigns" value={linkedCampaigns.length} />
      </div>

      {enrichmentStatus === "generating" && (
        <div className={`rounded-xl border border-violet-200/70 dark:border-violet-800/50 bg-violet-50/60 dark:bg-violet-950/20 p-3 flex items-center gap-3 ${nextDelay()} persona-section-card`} data-testid="strip-enrichment-generating">
          <Loader2 className="h-4 w-4 animate-spin text-violet-500 shrink-0" />
          <div>
            <p className="text-[13px] font-medium text-foreground">Completing this profile…</p>
            <p className="text-[11px] text-muted-foreground">Primary Pain, Desired Outcome, Deep Fear and more are being filled in. This usually takes a few minutes.</p>
          </div>
        </div>
      )}
      {enrichmentStatus === "failed" && (
        <div className={`rounded-xl border border-rose-200/70 dark:border-rose-800/50 bg-rose-50/60 dark:bg-rose-950/20 p-3 flex items-center justify-between gap-3 ${nextDelay()} persona-section-card`} data-testid="strip-enrichment-failed">
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="h-4 w-4 text-rose-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-[13px] font-medium text-foreground">Profile completion failed</p>
              {(persona as any).profileEnrichmentError && (
                <p className="text-[11px] text-muted-foreground truncate">{(persona as any).profileEnrichmentError}</p>
              )}
            </div>
          </div>
          <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => enrichProfileMutation.mutate()} disabled={enrichProfileMutation.isPending} data-testid="button-retry-enrichment">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
          </Button>
        </div>
      )}
      {isSlimProfile && enrichmentStatus !== "generating" && enrichmentStatus !== "failed" && (
        <div className={`rounded-xl border border-border/60 bg-muted/30 p-3 flex items-center justify-between gap-3 ${nextDelay()} persona-section-card`} data-testid="strip-enrichment-slim">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-500 shrink-0" />
            <p className="text-[13px] text-muted-foreground">This persona is missing its deep psychology fields (Primary Pain, Desired Outcome, Deep Fear…).</p>
          </div>
          <Button size="sm" className="rounded-full shrink-0" onClick={() => enrichProfileMutation.mutate()} disabled={enrichProfileMutation.isPending} data-testid="button-complete-profile">
            {enrichProfileMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
            Complete profile
          </Button>
        </div>
      )}

        <div className={`relative overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm p-5 md:p-6 ${nextDelay()} persona-section-card`} data-testid="card-persona-summary">
          <div className="pointer-events-none absolute inset-y-0 right-0 w-40 md:w-64 hidden sm:block" aria-hidden="true">
            <div className="absolute inset-0 bg-gradient-to-l from-violet-100/80 via-rose-50/50 to-transparent dark:from-violet-950/40 dark:via-rose-950/20" />
            <div className="absolute -right-8 top-1/2 -translate-y-1/2 h-40 w-40 rounded-full bg-gradient-to-br from-violet-200/70 to-rose-100/60 dark:from-violet-900/40 dark:to-rose-900/20 blur-2xl" />
            <div className="absolute right-8 top-1/2 -translate-y-1/2 h-16 w-16 rounded-2xl bg-gradient-to-br from-violet-300/50 to-rose-200/40 dark:from-violet-800/30 dark:to-rose-900/20 rotate-12" />
          </div>
          <div className="relative sm:pr-36 md:pr-56">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300">
                <Eye className="h-3.5 w-3.5" />
              </div>
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground/70">At a Glance</p>
            </div>
            <InlineTextarea
              value={persona.summary}
              onSave={(v) => save({ summary: v })}
              placeholder="Add a one-paragraph summary…"
              className="text-[14px] text-foreground/85 leading-relaxed"
              testId="persona-summary"
              rows={3}
            />
          </div>
        </div>

      <div ref={avatarCardRef} className={`rounded-2xl border border-border/60 bg-card shadow-sm p-4 md:p-5 ${nextDelay()} persona-section-card`} data-testid="card-persona-avatar">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="[&>div]:mb-0">
              <SectionHeader icon={Users} title={`Characters (${totalChars})`} accent="bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300" />
            </div>
            {isViewingPrimary ? (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5"
                data-testid="text-avatar-carousel-position"
              >
                <Star className="h-2.5 w-2.5 fill-current" />
                Primary
              </span>
            ) : currentLabel ? (
              <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60 truncate" data-testid="text-avatar-carousel-position">
                {currentLabel}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {isMasterAdmin && currentView.referenceSheetUrl ? (
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground/45 hover:text-violet-600"
                onClick={() => setSheetOpen(true)}
                data-testid="button-master-view-video-reference-sheet"
                data-character-id={currentChar?.id}
                aria-label={`View character reference sheet for ${currentChar?.name || "this character"}`}
                title={`View character reference sheet for ${currentChar?.name || "this character"}`}
              >
                <Eye className="h-3.5 w-3.5" />
              </Button>
            ) : null}
            {currentChar && !isViewingPrimary && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[11px]"
                onClick={() => setPrimaryMutation.mutate(currentChar.id)}
                disabled={setPrimaryMutation.isPending}
                data-testid="button-set-as-primary"
                title="Make this character the persona's primary avatar"
              >
                {setPrimaryMutation.isPending ? (
                  <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                ) : (
                  <Star className="h-3 w-3 mr-1.5" />
                )}
                Set as primary
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px] cursor-not-allowed opacity-60"
              disabled
              title="Coming soon"
              data-testid="button-send-character-top"
            >
              <Send className="h-3 w-3 mr-1.5" />
              Send to…
            </Button>
            {currentChar && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[11px] text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setConfirmDeleteCharId(currentChar.id)}
                disabled={deleteCharMutation.isPending}
                data-testid="button-delete-character-top"
              >
                <Trash2 className="h-3 w-3 mr-1.5" />
                Delete
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-start gap-5 pb-5 mb-4 border-b border-border/50 flex-wrap sm:flex-nowrap">
          <div className="flex items-center gap-2 shrink-0">
            {totalChars > 1 && (
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-full flex-shrink-0"
                onClick={goPrevChar}
                aria-label="Previous character"
                data-testid="button-avatar-carousel-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            <button
              type="button"
              onClick={() => currentView.imageUrl && setAvatarOpen(true)}
              disabled={!currentView.imageUrl}
              className={`w-48 h-48 md:w-56 md:h-56 rounded-2xl overflow-hidden bg-muted/40 flex items-center justify-center border border-border/40 shadow-sm ring-1 ring-black/[0.03] ${currentView.imageUrl ? "cursor-zoom-in hover:ring-2 hover:ring-primary/30 transition-all" : "cursor-default"}`}
              data-testid="button-open-avatar-lightbox"
              title={currentView.imageUrl ? "View larger" : undefined}
            >
              {currentView.imageUrl ? (
                <img src={currentView.imageUrl} alt={currentView.name || persona.name} className="w-full h-full object-cover" data-testid="img-persona-avatar-large" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-muted-foreground/60">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="text-[11px]">Rendering avatar…</span>
                </div>
              )}
            </button>
            {totalChars > 1 && (
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-full flex-shrink-0"
                onClick={goNextChar}
                aria-label="Next character"
                data-testid="button-avatar-carousel-next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="flex-1 min-w-0 basis-full sm:basis-auto flex flex-col items-start gap-3">
            <div>
              {avatarNameDraft !== null ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={avatarNameDraft}
                    onChange={(e) => setAvatarNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveAvatarNameMutation.mutate(avatarNameDraft);
                      if (e.key === "Escape") setAvatarNameDraft(null);
                    }}
                    placeholder="e.g. Maya, Jordan, Alex…"
                    className="max-w-xs h-10 text-base"
                    autoFocus
                    data-testid="input-avatar-name"
                  />
                  <Button size="sm" onClick={() => saveAvatarNameMutation.mutate(avatarNameDraft)} disabled={saveAvatarNameMutation.isPending} data-testid="button-save-avatar-name">
                    {saveAvatarNameMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setAvatarNameDraft(null)} data-testid="button-cancel-avatar-name">Cancel</Button>
                </div>
              ) : bioEditingId && currentChar && bioEditingId === currentChar.id && bioDraft ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={bioDraft.name}
                    onChange={(e) => setBioDraft({ ...bioDraft, name: e.target.value })}
                    placeholder="Character name"
                    className="max-w-xs h-10 text-base font-semibold"
                    autoFocus
                    data-testid="input-bio-name"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-0.5">
                  <h3 className="text-[17px] font-semibold tracking-tight text-foreground" data-testid="text-avatar-name">
                    {currentView.name || <span className="text-muted-foreground/60 italic font-normal">Unnamed</span>}
                  </h3>
                  {currentChar ? (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 ml-1" onClick={startBioEdit} data-testid="button-edit-bio" title="Edit bio">
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                  ) : (isViewingPrimary || !currentChar) ? (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 ml-1" onClick={() => setAvatarNameDraft(currentView.name || "")} data-testid="button-edit-avatar-name" title={currentView.name ? "Rename" : "Add name"}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                  ) : null}
                </div>
              )}
            </div>

            {(currentDisplay.age || currentDisplay.genderPresentation || currentDisplay.ethnicityHeritage || currentDisplay.regionOrSetting) && (
              <div className="flex flex-wrap items-center gap-1.5 max-w-full" data-testid="row-inline-bio-chips">
                {currentDisplay.age && (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background px-2.5 py-1 text-[11px] font-medium text-foreground/80 shadow-sm"
                    data-testid="badge-top-age"
                  >
                    <Cake className="h-3 w-3 text-violet-500" />
                    <span className="whitespace-normal text-left leading-tight">{currentDisplay.age}</span>
                  </span>
                )}
                {currentDisplay.genderPresentation && (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background px-2.5 py-1 text-[11px] font-medium text-foreground/80 shadow-sm"
                    data-testid="badge-top-gender"
                  >
                    <Users className="h-3 w-3 text-violet-500" />
                    <span className="whitespace-normal text-left leading-tight">{currentDisplay.genderPresentation}</span>
                  </span>
                )}
                {currentDisplay.ethnicityHeritage && (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background px-2.5 py-1 text-[11px] font-medium text-foreground/80 shadow-sm"
                    data-testid="badge-top-ethnicity"
                  >
                    <Globe2 className="h-3 w-3 text-violet-500" />
                    <span className="whitespace-normal text-left leading-tight">{currentDisplay.ethnicityHeritage}</span>
                  </span>
                )}
                {currentDisplay.regionOrSetting && (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background px-2.5 py-1 text-[11px] font-medium text-foreground/80 shadow-sm"
                    data-testid="badge-top-region"
                  >
                    <MapPin className="h-3 w-3 text-violet-500" />
                    <span className="whitespace-normal text-left leading-tight">{currentDisplay.regionOrSetting}</span>
                  </span>
                )}
              </div>
            )}

            <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setBioExpanded((v) => !v)}
            className="h-8 text-[12px]"
            data-testid={bioExpanded ? "button-hide-primary-bio-top" : "button-show-primary-bio"}
          >
            {!bioExpanded && currentBioStatus === "generating" ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Reading bio from avatar…
              </>
            ) : !bioExpanded && currentBioStatus === "failed" ? (
              <>
                <ImageOff className="h-3.5 w-3.5 mr-1.5 text-destructive" />
                <span className="text-destructive">Bio failed — show details</span>
              </>
            ) : bioExpanded ? (
              <>
                Hide details
                <ChevronUp className="h-3.5 w-3.5 ml-1.5" />
              </>
            ) : (
              <>
                Show details
                <ChevronDown className="h-3.5 w-3.5 ml-1.5" />
              </>
            )}
          </Button>
          </div>
        </div>

        {currentChar && (
          <div className="rounded-xl border border-violet-200/60 dark:border-violet-900/50 bg-violet-50/40 dark:bg-violet-950/20 p-3 mb-5 flex flex-wrap items-center justify-between gap-3" data-testid="section-ad-identity-pack-status" data-character-id={currentChar.id}>
            <div>
              <p className="text-[12px] font-semibold">Ads Express identity pack <span className="font-normal text-muted-foreground">· {currentChar.name}</span></p>
              <p className="text-[11px] text-muted-foreground mt-1" data-testid="text-ad-identity-pack-status" data-status={adIdentityPackState}>
                {adIdentityPackState === "ready" ? "Ready · 3 character images" :
                  adIdentityPackState === "generating" ? "Generating · An eligible Ads Express run creates this pack automatically. Images will appear here when ready." :
                  adIdentityPackState === "failed" ? "Failed to create character images. This does not mean your ad failed." :
                  adIdentityPackState === "stale" ? "Avatar changed · The previous pack is out of date and is not shown as current. An eligible Ads Express run creates a new pack automatically." :
                  adIdentityPackState === "interrupted" ? "Generation was interrupted. An eligible Ads Express run creates the pack automatically." :
                  "Not created yet · An eligible Ads Express run creates this pack automatically."}
              </p>
              {adIdentityPackState === "failed" && currentView.adIdentityPackError && (
                <p className="text-[11px] text-destructive mt-1">{currentView.adIdentityPackError}</p>
              )}
            </div>
            {adIdentityPackReady && (
              <Button size="sm" variant="outline" onClick={openAdIdentityPack}
                data-testid="button-view-ad-identity-pack" data-character-id={currentChar.id}>
                <Eye className="h-3.5 w-3.5 mr-1.5" /> View Ads Express images
              </Button>
            )}
            {isMasterAdmin && !adIdentityPackReady && (
              <Button size="sm" variant="outline" onClick={openAdIdentityPack}
                data-testid="button-manage-ad-identity-pack">Manage pack</Button>
            )}
          </div>
        )}

        {bioExpanded && (
          <div
            id="primary-character-bio"
            className="pb-6 mb-6 border-b border-border/50 space-y-4 text-[13px]"
            data-testid="section-primary-character-bio"
          >
            {currentBioStatus === "generating" ? (
              <div className="rounded-lg border border-dashed border-violet-300/60 bg-violet-50/40 dark:bg-violet-950/20 px-3 py-4 flex items-center gap-2.5 text-[12px] text-violet-900 dark:text-violet-100">
                <Loader2 className="h-4 w-4 animate-spin" />
                <div className="leading-tight">
                  <div className="font-medium">Reading bio from avatar…</div>
                  <div className="text-[11px] text-muted-foreground/80">The vision workflow infers age, gender, ethnicity, wardrobe, and presence from this character&rsquo;s image. Usually takes 20&ndash;40 seconds.</div>
                </div>
              </div>
            ) : null}

            {currentChar && (currentChar as any).userCharacterDirection && (
              <div
                className="rounded-lg border border-sky-200/70 dark:border-sky-900/50 bg-sky-50/60 dark:bg-sky-950/20 px-3 py-2.5"
                data-testid="section-character-direction"
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Sparkles className="h-3 w-3 text-sky-600 dark:text-sky-300" />
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-sky-700 dark:text-sky-300">
                    Generated from your description
                  </p>
                </div>
                <p
                  className="text-[12px] text-foreground/85 leading-relaxed whitespace-pre-line"
                  data-testid="text-character-direction"
                >
                  {(currentChar as any).userCharacterDirection}
                </p>
              </div>
            )}

            {currentBioStatus === "failed" && currentChar ? (
              <div className="flex flex-col items-start gap-2 p-3 rounded-lg bg-destructive/5 border border-destructive/20">
                <span className="text-[12px] text-destructive font-medium">Vision bio failed after 3 attempts</span>
                {currentBioError && (
                  <span className="text-[11px] text-muted-foreground/70 line-clamp-3">{currentBioError}</span>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px]"
                  onClick={() => retryBioMutation.mutate(currentChar.id)}
                  disabled={retryBioMutation.isPending}
                  data-testid="button-retry-primary-character-bio"
                >
                  <RefreshCw className="h-3 w-3 mr-1.5" />
                  Retry bio
                </Button>
              </div>
            ) : null}

            {bioEditingId && currentChar && bioEditingId === currentChar.id && bioDraft ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 mb-1">Age</p>
                    <Input
                      value={bioDraft.age}
                      onChange={(e) => setBioDraft({ ...bioDraft, age: e.target.value })}
                      placeholder="e.g. 32"
                      className="h-8 text-[12px]"
                      data-testid="input-bio-age"
                    />
                  </div>
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 mb-1">Gender</p>
                    <Input
                      value={bioDraft.genderPresentation}
                      onChange={(e) => setBioDraft({ ...bioDraft, genderPresentation: e.target.value })}
                      placeholder="e.g. female"
                      className="h-8 text-[12px]"
                      data-testid="input-bio-gender"
                    />
                  </div>
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 mb-1">Ethnicity</p>
                    <Input
                      value={bioDraft.ethnicityHeritage}
                      onChange={(e) => setBioDraft({ ...bioDraft, ethnicityHeritage: e.target.value })}
                      placeholder="e.g. Latinx"
                      className="h-8 text-[12px]"
                      data-testid="input-bio-ethnicity"
                    />
                  </div>
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 mb-1">Region</p>
                    <Input
                      value={bioDraft.regionOrSetting}
                      onChange={(e) => setBioDraft({ ...bioDraft, regionOrSetting: e.target.value })}
                      placeholder="e.g. urban US"
                      className="h-8 text-[12px]"
                      data-testid="input-bio-region"
                    />
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 mb-1">Image prompt</p>
                  <Textarea
                    value={bioDraft.imagePromptSummary}
                    onChange={(e) => setBioDraft({ ...bioDraft, imagePromptSummary: e.target.value })}
                    placeholder="One-line camera/lighting/setting summary…"
                    className="min-h-[64px] text-[12px]"
                    data-testid="input-bio-image-prompt"
                  />
                </div>
                {(["physicalLook", "styleAndWardrobe", "cameraPresence", "visualDistinction"] as const).map((key) => {
                  const labels: Record<string, string> = {
                    physicalLook: "Physical look",
                    styleAndWardrobe: "Style & wardrobe",
                    cameraPresence: "Camera presence",
                    visualDistinction: "Visual distinction",
                  };
                  const rows = bioDraft[key];
                  const update = (next: Array<{ k: string; v: string }>) =>
                    setBioDraft({ ...bioDraft, [key]: next });
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">{labels[key]}</p>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-6 text-[11px] px-2"
                          onClick={() => update([...rows, { k: "", v: "" }])}
                          data-testid={`button-bio-add-${key}`}
                        >
                          <Plus className="h-3 w-3 mr-1" /> Add field
                        </Button>
                      </div>
                      <div className="space-y-1.5">
                        {rows.length === 0 ? (
                          <p className="text-[11px] text-muted-foreground/50 italic">No fields yet. Use Add field to start.</p>
                        ) : rows.map((row, i) => (
                          <div key={i} className="flex items-start gap-1.5">
                            <Input
                              value={row.k}
                              onChange={(e) => update(rows.map((r, j) => j === i ? { ...r, k: e.target.value } : r))}
                              placeholder="field"
                              className="h-8 text-[12px] w-40 flex-shrink-0"
                              data-testid={`input-bio-${key}-key-${i}`}
                            />
                            <Textarea
                              value={row.v}
                              onChange={(e) => update(rows.map((r, j) => j === i ? { ...r, v: e.target.value } : r))}
                              placeholder="value"
                              className="min-h-[32px] text-[12px] flex-1 py-1.5"
                              rows={1}
                              data-testid={`input-bio-${key}-value-${i}`}
                            />
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 flex-shrink-0 text-muted-foreground hover:text-destructive"
                              onClick={() => update(rows.filter((_, j) => j !== i))}
                              aria-label="Remove field"
                              data-testid={`button-bio-${key}-remove-${i}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/40">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={cancelBioEdit}
                    disabled={saveBioMutation.isPending}
                    data-testid="button-bio-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => saveBioMutation.mutate()}
                    disabled={saveBioMutation.isPending}
                    data-testid="button-bio-save"
                  >
                    {saveBioMutation.isPending ? (
                      <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving…</>
                    ) : (
                      <><Check className="h-3.5 w-3.5 mr-1.5" /> Save bio</>
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 mb-1">Image prompt</p>
                  <p className="text-foreground/85 leading-relaxed" data-testid="text-inline-primary-image-prompt">
                    {currentDisplay.imagePromptSummary ?? <span className="text-muted-foreground/50">—</span>}
                  </p>
                </div>
                {(["physicalLook", "styleAndWardrobe", "cameraPresence", "visualDistinction"] as const).map((key) => {
                  const val = currentDisplay[key] as Record<string, any> | null;
                  const labels: Record<string, string> = {
                    physicalLook: "Physical look",
                    styleAndWardrobe: "Style & wardrobe",
                    cameraPresence: "Camera presence",
                    visualDistinction: "Visual distinction",
                  };
                  const entries = val && typeof val === "object" ? Object.entries(val) : [];
                  return (
                    <div key={key}>
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 mb-2">{labels[key]}</p>
                      {entries.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                          {entries.map(([k, v]) => (
                            <div key={k} className="text-[12px]">
                              <span className="text-muted-foreground/70">{k.replace(/_/g, " ")}: </span>
                              <span className="text-foreground/85">{Array.isArray(v) ? v.join(", ") : String(v ?? "")}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[12px] text-muted-foreground/50">—</p>
                      )}
                    </div>
                  );
                })}

                <div className="flex items-center justify-between pt-1">
                  {currentChar ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px]"
                      onClick={startBioEdit}
                      data-testid="button-edit-bio-inline"
                    >
                      <Edit className="h-3 w-3 mr-1.5" />
                      Edit bio
                    </Button>
                  ) : <span />}
                  <button
                    type="button"
                    onClick={() => setBioExpanded(false)}
                    className="text-[11px] text-muted-foreground/70 hover:text-foreground transition-colors inline-flex items-center gap-1"
                    data-testid="button-hide-primary-bio"
                  >
                    Hide bio details
                    <ChevronUp className="h-3 w-3" />
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {bioExpanded && (
        <div>
          <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60 mb-3">Character Reference Sheet</p>
          <div>
              {currentView.referenceSheetStatus === "generating" ? (
                <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Generating reference sheet… (this can take 2–3 minutes)
                </div>
              ) : currentView.referenceSheetStatus === "ready" && currentView.referenceSheetUrl ? (
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => setSheetOpen(true)}
                    aria-label="Open character reference sheet at full size"
                    className="w-full rounded-lg overflow-hidden border border-border/40 bg-white hover:ring-2 hover:ring-primary/30 transition-all"
                    data-testid="button-view-reference-sheet"
                  >
                    <img
                      src={currentView.referenceSheetUrl}
                      alt="Character reference sheet"
                      className="w-full h-auto block"
                    />
                  </button>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSheetOpen(true)}
                      data-testid="button-open-reference-sheet"
                    >
                      <Maximize2 className="h-3.5 w-3.5 mr-1.5" />
                      View full size
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => generateSheetMutation.mutate(currentChar?.id ?? null)}
                      disabled={generateSheetMutation.isPending || !currentView.imageUrl}
                      data-testid="button-regenerate-reference-sheet"
                    >
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                      Regenerate
                    </Button>
                  </div>
                </div>
              ) : currentView.referenceSheetStatus === "failed" ? (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-[12px] text-destructive">
                    <ImageOff className="h-3.5 w-3.5" />
                    Reference sheet failed to generate.
                  </div>
                  {currentView.referenceSheetError && (
                    <p className="text-[11px] text-muted-foreground/70 line-clamp-2">{currentView.referenceSheetError}</p>
                  )}
                  <div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => generateSheetMutation.mutate(currentChar?.id ?? null)}
                      disabled={generateSheetMutation.isPending || !currentView.imageUrl}
                      data-testid="button-retry-reference-sheet"
                    >
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                      Try again
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <Button
                    size="sm"
                    onClick={() => generateSheetMutation.mutate(currentChar?.id ?? null)}
                    disabled={generateSheetMutation.isPending || !currentView.imageUrl}
                    data-testid="button-generate-reference-sheet"
                  >
                    <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                    Generate Character Reference Sheet
                  </Button>
                  {!currentView.imageUrl && (
                    <p className="text-[11px] text-muted-foreground/70 mt-1.5">Wait for the avatar to finish rendering first.</p>
                  )}
                </div>
              )}
          </div>
        </div>
        )}

        {currentChar && (
          <Dialog open={adIdentityPackOpen && adIdentityPackCharacterId === currentChar.id} onOpenChange={setAdIdentityPackOpen}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dialog-ad-identity-pack">
              <DialogHeader>
                <DialogTitle>Ads Express Identity Pack — {currentChar.name || "Character"}</DialogTitle>
              </DialogHeader>
          <div data-testid="section-ad-identity-pack">
            <p className="text-[11px] text-muted-foreground mb-3">Static-ad identity images · Separate from the Video Reference character sheet.</p>
            {adIdentityPackState === "generating" ? (
              <div className="flex items-center gap-2 text-[12px] text-muted-foreground mb-3">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Generating identity pack… An eligible Ads Express run creates it automatically.
              </div>
            ) : null}
            {adIdentityPackReady && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3" data-testid="grid-ad-identity-pack-images">
                {currentView.adIdentityPackUrls.map((url, index) => (
                  <figure key={`${url}-${index}`}>
                    <img src={url} alt={`Ads Express ${AD_IDENTITY_IMAGE_ROLES[index]} of ${currentChar.name}`}
                      className="w-full h-auto rounded-lg border border-border/40 bg-white"
                      data-testid={`img-ad-identity-pack-${index + 1}`} />
                    <figcaption className="text-[11px] font-medium mt-1">{AD_IDENTITY_IMAGE_ROLES[index]}</figcaption>
                  </figure>
                ))}
              </div>
            )}
            {adIdentityPackState !== "ready" && adIdentityPackState !== "generating" && (
              <div className="flex items-center gap-2 text-[12px] text-destructive mb-2">
                <ImageOff className="h-3.5 w-3.5" />
                {adIdentityPackState === "failed" ? "Character images failed to generate; this does not mean your ad failed." :
                  adIdentityPackState === "stale" ? "Avatar changed. The old identity pack is not current." :
                  adIdentityPackState === "interrupted" ? "Identity-pack generation was interrupted." :
                  "No identity pack yet. An eligible Ads Express run creates one automatically."}
              </div>
            )}
            {currentView.adIdentityPackError && adIdentityPackState === "failed" && (
              <p className="text-[11px] text-muted-foreground/70 mb-2">{currentView.adIdentityPackError}</p>
            )}
            {isMasterAdmin && <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={currentView.adIdentityPackUrls.length > 0 ? "outline" : "default"}
                onClick={() => generateAdIdentityPackMutation.mutate(currentChar.id)}
                disabled={
                  generateAdIdentityPackMutation.isPending
                  || adIdentityPackIsActive
                  || !currentView.imageUrl
                }
                data-testid={currentView.adIdentityPackUrls.length > 0 ? "button-regenerate-ad-identity-pack" : "button-generate-ad-identity-pack"}
              >
                {generateAdIdentityPackMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : currentView.adIdentityPackUrls.length > 0 ? (
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                )}
                {currentView.adIdentityPackUrls.length > 0 ? "Regenerate" : "Generate Ads Express Identity Pack"}
              </Button>
              {isMasterAdmin && (currentView.adIdentityPackUrls.length > 0 || adIdentityPackIsInterrupted) && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setDeleteAdIdentityPackOpen(true)}
                  disabled={
                    deleteAdIdentityPackMutation.isPending
                    || adIdentityPackIsActive
                  }
                  data-testid="button-delete-ad-identity-pack"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  {adIdentityPackIsInterrupted ? "Reset interrupted generation" : "Delete"}
                </Button>
              )}
            </div>}
            {!currentView.imageUrl && (
              <p className="text-[11px] text-muted-foreground/70 mt-1.5">Wait for the avatar to finish rendering first.</p>
            )}
          </div>
            </DialogContent>
          </Dialog>
        )}

        <CharactersSection
          personaId={persona.id}
          selectedCharacterId={currentChar?.id ?? null}
          onSelectCharacter={handleSelectCharacterFromGrid}
        />
      </div>

      <Dialog open={sheetOpen} onOpenChange={setSheetOpen}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] p-0 gap-0 flex flex-col" data-testid="dialog-reference-sheet">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle>Character Reference Sheet — {currentView.name || persona.name}</DialogTitle>
          </DialogHeader>
          {currentView.referenceSheetUrl && (
            <div className="flex-1 overflow-auto bg-muted/30 p-4 flex items-center justify-center min-h-0">
              <img
                src={currentView.referenceSheetUrl}
                alt="Character reference sheet"
                className="max-w-full max-h-full object-contain rounded-md shadow-sm bg-white"
              />
            </div>
          )}
          <DialogFooter className="px-6 py-4 border-t">
            {currentView.referenceSheetUrl && (
              <Button
                variant="outline"
                asChild
                data-testid="button-download-reference-sheet"
              >
                <a
                  href={currentView.referenceSheetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={`character-reference-sheet-${currentView.name || persona.name || persona.id}.png`}
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  Open / Download
                </a>
              </Button>
            )}
            <Button variant="outline" onClick={() => setSheetOpen(false)} data-testid="button-close-reference-sheet">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={videoReferenceSheetOpen} onOpenChange={setVideoReferenceSheetOpen}>
        <DialogContent
          className="h-[95dvh] max-h-[95dvh] w-[95vw] max-w-[95vw] overflow-hidden p-0 gap-0 flex flex-col"
          data-testid="dialog-video-reference-sheet-snapshot"
        >
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle>
              Video Reference character sheet — {currentChar?.name || "Character"}
            </DialogTitle>
          </DialogHeader>
          {videoReferenceSheetSnapshot?.url ? (
            <div className="relative flex-1 min-h-0 overflow-hidden bg-muted/30 p-4">
              <img
                src={videoReferenceSheetSnapshot.url}
                alt="Multi-angle identity sheet used by Video Reference"
                className="h-full w-full object-contain rounded-md shadow-sm bg-white"
                data-testid="img-video-reference-sheet-snapshot"
              />
              {currentView.referenceSheetStatus === "generating" ? (
                <div
                  className="absolute inset-0 flex items-center justify-center bg-black/45 backdrop-blur-[1px]"
                  data-testid="overlay-video-reference-sheet-regenerating"
                >
                  <div className="flex items-center gap-2 rounded-lg bg-background/95 px-4 py-3 text-sm font-medium shadow-lg">
                    <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
                    Regenerating for future runs…
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          <DialogFooter className="px-6 py-4 border-t sm:justify-between">
            <div>
              <p className="text-[10px] text-muted-foreground">
                Exact immutable sheet used by run {videoReferenceSheetSnapshot?.runId?.slice(0, 8)}
              </p>
              <p className="text-[10px] text-muted-foreground">
                Regenerate or delete affects future runs only.
              </p>
            </div>
            <div className="flex gap-2">
              {currentChar ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => generateSheetMutation.mutate(currentChar.id)}
                    disabled={
                      generateSheetMutation.isPending
                      || currentView.referenceSheetStatus === "generating"
                      || !currentView.imageUrl
                    }
                    data-testid="button-master-regenerate-video-reference-sheet"
                  >
                    {generateSheetMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Regenerate for future runs
                  </Button>
                  <Button
                    variant="outline"
                    className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => setDeleteVideoReferenceSheetOpen(true)}
                    disabled={
                      deleteVideoReferenceSheetMutation.isPending
                      || currentView.referenceSheetStatus === "generating"
                    }
                    data-testid="button-master-delete-video-reference-sheet"
                  >
                    {deleteVideoReferenceSheetMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Delete cached sheet
                  </Button>
                </>
              ) : null}
              {videoReferenceSheetSnapshot?.url ? (
                <Button variant="outline" asChild>
                  <a
                    href={videoReferenceSheetSnapshot.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    Open / Download
                  </a>
                </Button>
              ) : null}
              <Button variant="outline" onClick={() => setVideoReferenceSheetOpen(false)}>
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteVideoReferenceSheetOpen}
        onOpenChange={setDeleteVideoReferenceSheetOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this cached identity sheet?</AlertDialogTitle>
            <AlertDialogDescription>
              Future Video Reference runs will no longer reuse it. The next eligible run will
              generate a new sheet and may incur a generation charge. Existing runs keep the
              immutable sheet they originally used.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteVideoReferenceSheetMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (currentChar) {
                  deleteVideoReferenceSheetMutation.mutate(currentChar.id);
                }
              }}
              disabled={!currentChar || deleteVideoReferenceSheetMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-video-reference-sheet"
            >
              {deleteVideoReferenceSheetMutation.isPending ? "Deleting…" : "Delete cached sheet"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteAdIdentityPackOpen}
        onOpenChange={setDeleteAdIdentityPackOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete the Ads Express identity pack?</AlertDialogTitle>
            <AlertDialogDescription>
              Ads Express will no longer reuse these character images. You can generate a new pack at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAdIdentityPackMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (currentChar) deleteAdIdentityPackMutation.mutate(currentChar.id);
              }}
              disabled={!currentChar || deleteAdIdentityPackMutation.isPending || adIdentityPackIsActive}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-ad-identity-pack"
            >
              {deleteAdIdentityPackMutation.isPending ? "Deleting…" : "Delete identity pack"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDeleteCharId} onOpenChange={(o) => !o && setConfirmDeleteCharId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this character?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes the character and their avatar. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-cancel-delete-character">Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  if (confirmDeleteCharId) {
                    deleteCharMutation.mutate(confirmDeleteCharId);
                    setConfirmDeleteCharId(null);
                  }
                }}
                data-testid="button-confirm-delete-character"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

      <div className={`rounded-2xl border border-border/60 bg-card shadow-sm p-5 ${nextDelay()} persona-section-card`}>
        <SectionHeader icon={Brain} title="Psychology Profile" accent="bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300" />
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <PsychField label="Motivation" content={persona.worldview} icon={Compass} tint="indigo" onSave={(v) => save({ worldview: v })} />
          <PsychField label="Primary Pain" content={persona.primaryPain} icon={Flame} tint="rose" onSave={(v) => save({ primaryPain: v })} />
          <PsychField label="Desired Outcome" content={persona.desiredOutcome} icon={Sprout} tint="emerald" onSave={(v) => save({ desiredOutcome: v })} />
          <PsychField label="Deep Fear" content={persona.deepFear} icon={AlertTriangle} tint="amber" onSave={(v) => save({ deepFear: v })} />
        </div>
      </div>

      <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 ${nextDelay()}`} data-testid="persona-section-grid">
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-5 persona-section-card" data-testid="card-beliefs">
          <SectionHeader icon={Lightbulb} title="Core Beliefs" count={listOf(persona.coreBeliefsJson).length} accent="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" />
          <InlineStringList items={listOf(persona.coreBeliefsJson)} onSave={(v) => save({ coreBeliefsJson: v })} placeholder="Add a core belief" addLabel="Add belief" testId="persona-beliefs" />
        </div>

        <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-5 persona-section-card" data-testid="card-objections">
          <SectionHeader icon={ShieldAlert} title="Objections" count={listOf(persona.objectionsJson).length} accent="bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300" />
          <InlineStringList items={listOf(persona.objectionsJson)} onSave={(v) => save({ objectionsJson: v })} placeholder="Add an objection" addLabel="Add objection" testId="persona-objections" />
        </div>

        <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-5 persona-section-card" data-testid="card-trust-triggers">
          <SectionHeader icon={Shield} title="Trust Triggers" count={listOf(persona.trustTriggersJson).length} accent="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" />
          <InlineStringList items={listOf(persona.trustTriggersJson)} onSave={(v) => save({ trustTriggersJson: v })} placeholder="Add a trust trigger" addLabel="Add trigger" testId="persona-trust-triggers" />
        </div>

        <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-5 persona-section-card" data-testid="card-purchase-drivers">
          <SectionHeader icon={TrendingUp} title="Purchase Drivers" count={listOf(persona.purchaseDriversJson).length} accent="bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300" />
          <InlineStringList items={listOf(persona.purchaseDriversJson)} onSave={(v) => save({ purchaseDriversJson: v })} placeholder="Add a purchase driver" addLabel="Add driver" testId="persona-purchase-drivers" />
        </div>

        <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-5 persona-section-card" data-testid="card-emotional-hooks">
          <SectionHeader icon={Heart} title="Emotional Hooks" count={listOf(persona.emotionalHooksJson).length} accent="bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300" />
          <InlineStringList items={listOf(persona.emotionalHooksJson)} onSave={(v) => save({ emotionalHooksJson: v })} placeholder="Add an emotional hook" addLabel="Add hook" testId="persona-emotional-hooks" />
        </div>

        <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-5 persona-section-card" data-testid="card-secondary-pains">
          <SectionHeader icon={Target} title="Secondary Pains" count={listOf(persona.secondaryPainsJson).length} accent="bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" />
          <InlineStringList items={listOf(persona.secondaryPainsJson)} onSave={(v) => save({ secondaryPainsJson: v })} placeholder="Supporting frustrations" addLabel="Add pain" testId="persona-secondary-pains" />
        </div>

        <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-5 persona-section-card" data-testid="card-language">
          <SectionHeader icon={MessageCircle} title="Language Patterns" count={listOf(persona.languagePatternsJson).length} accent="bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300" />
          <InlineStringList items={listOf(persona.languagePatternsJson)} onSave={(v) => save({ languagePatternsJson: v })} variant="chips" tinted placeholder="Words/phrases they use" addLabel="Add phrase" testId="persona-language" />
        </div>

        <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-5 persona-section-card" data-testid="card-exclusions">
          <SectionHeader icon={Ban} title="Exclusions" count={listOf(persona.exclusionsJson).length} accent="bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300" />
          <InlineStringList items={listOf(persona.exclusionsJson)} onSave={(v) => save({ exclusionsJson: v })} placeholder="Frames to avoid" addLabel="Add exclusion" testId="persona-exclusions" />
        </div>
      </div>

      <ResearchSection
        personaId={persona.id}
        personaGenerating={persona.status === "generating" || (persona as any).researchStatus === "generating"}
      />

      <div className={`grid grid-cols-1 lg:grid-cols-3 gap-4 lg:items-stretch ${nextDelay()}`} data-testid="persona-bottom-row">
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-5 persona-section-card h-full flex flex-col" data-testid="card-customer-voice">
          <SectionHeader icon={Quote} title="Customer Voice" count={listOf(persona.customerVoiceQuotesJson).length} accent="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" />
          <InlineStringList items={listOf(persona.customerVoiceQuotesJson)} onSave={(v) => save({ customerVoiceQuotesJson: v })} variant="quotes" placeholder="Real customer language" addLabel="Add quote" testId="persona-voice" />
        </div>

        <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-5 persona-section-card h-full flex flex-col" data-testid="card-notes">
          <SectionHeader icon={FileText} title="Notes" accent="bg-muted text-muted-foreground" />
          <InlineTextarea
            value={persona.notes}
            onSave={(v) => save({ notes: v })}
            placeholder="Add internal notes…"
            className="text-[13px] text-foreground/75 leading-relaxed"
            testId="persona-notes"
            rows={4}
          />
        </div>

        <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-5 persona-section-card h-full flex flex-col" data-testid="card-campaigns">
          <div className="flex items-center justify-between gap-2">
            <SectionHeader icon={Crosshair} title="Linked Campaigns" count={linkedCampaigns.length} accent="bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300" />
            {linkedCampaigns.length > 0 && (
              <Link href={`/brands/${brandId}/campaigns`}>
                <Button size="sm" className="text-[11px] rounded-full h-7 px-3 -mt-4" data-testid="button-create-campaign-from-persona">
                  <Megaphone className="h-3 w-3 mr-1.5" />
                  New Campaign
                </Button>
              </Link>
            )}
          </div>
          {linkedCampaigns.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center py-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-50 to-rose-50 dark:from-violet-950/30 dark:to-rose-950/20 border border-violet-100/70 dark:border-violet-900/40 mb-3">
                <Megaphone className="h-6 w-6 text-violet-300 dark:text-violet-700" />
              </div>
              <p className="text-sm text-muted-foreground/70 font-medium">No campaigns yet</p>
              <p className="text-[12px] text-muted-foreground/50 mt-1">Create campaigns that talk to this persona</p>
              <Link href={`/brands/${brandId}/campaigns`}>
                <Button size="sm" className="mt-4 text-[12px] rounded-lg" data-testid="button-create-campaign-from-persona">
                  <Megaphone className="h-3 w-3 mr-1.5" />
                  New Campaign
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-1.5">
              {linkedCampaigns.map(c => (
                <Link key={c.id} href={`/brands/${brandId}/campaigns/${c.id}`}>
                  <div className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-border/50 bg-muted/20 hover:bg-muted/50 hover:border-border transition-colors cursor-pointer group" data-testid={`link-campaign-${c.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Megaphone className="h-3 w-3 text-muted-foreground/60 shrink-0 group-hover:text-primary transition-colors" />
                      <span className="text-[13px] font-medium text-foreground/85 truncate">{c.name}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge
                        variant="outline"
                        className={`text-[10px] rounded-full px-2 py-0 border ${c.status === "active" ? "border-emerald-300 text-emerald-600" : "border-border text-muted-foreground"}`}
                      >
                        {c.status}
                      </Badge>
                      <ChevronRight className="h-3 w-3 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={avatarOpen} onOpenChange={setAvatarOpen}>
        <DialogContent className="max-w-2xl p-2 bg-transparent border-0 shadow-none" data-testid="dialog-primary-avatar">
          <DialogHeader className="sr-only">
            <DialogTitle>{currentView.name || persona.name} avatar</DialogTitle>
          </DialogHeader>
          {currentView.imageUrl ? (
            <img
              src={currentView.imageUrl}
              alt={currentView.name || persona.name}
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
              data-testid="img-primary-avatar-large"
            />
          ) : (
            <div className="w-full aspect-square max-w-md mx-auto rounded-lg overflow-hidden bg-muted/40 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2 text-muted-foreground/60">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="text-[11px]">Rendering avatar…</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
    </div>
  );
}

interface PersonaResearchView {
  status: string | null;
  error: string | null;
  topics: PersonaResearchTopicView[];
}

function ResearchTopicStatusBadge({ status }: { status: string }) {
  if (status === "researching") {
    return (
      <Badge variant="secondary" className="text-[10px] gap-1 shrink-0 bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
        <Loader2 className="h-2.5 w-2.5 animate-spin" />
        Researching
      </Badge>
    );
  }
  if (status === "done") {
    return (
      <Badge variant="secondary" className="text-[10px] gap-1 shrink-0 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
        <Check className="h-2.5 w-2.5" />
        Done
      </Badge>
    );
  }
  if (status === "failed") {
    return (
      <Badge variant="secondary" className="text-[10px] gap-1 shrink-0 bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
        <AlertTriangle className="h-2.5 w-2.5" />
        Failed
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="text-[10px] shrink-0 text-muted-foreground">
      Queued
    </Badge>
  );
}

function ResearchTopicCard({ topic, index }: { topic: PersonaResearchTopicView; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const sources: string[] = Array.isArray(topic.sourcesJson)
    ? (topic.sourcesJson as unknown[]).filter((s): s is string => typeof s === "string" && !!s.trim())
    : [];
  const findings = topic.findings || "";
  const isLong = findings.length > 700;

  return (
    <div className="rounded-xl border border-border/50 bg-card/50 p-4 space-y-2.5" data-testid={`research-topic-${index}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-foreground leading-snug" data-testid={`research-topic-title-${index}`}>
            {topic.topic}
          </p>
          {topic.description && (
            <p className="text-[12px] text-muted-foreground leading-relaxed mt-0.5">{topic.description}</p>
          )}
        </div>
        <ResearchTopicStatusBadge status={topic.status} />
      </div>

      {topic.status === "failed" && topic.error && (
        <p className="text-[11px] text-rose-600 dark:text-rose-400 leading-relaxed" data-testid={`research-topic-error-${index}`}>
          {topic.error}
        </p>
      )}

      {topic.status === "done" && findings && (
        <p
          className={`text-[13px] text-foreground/80 leading-relaxed whitespace-pre-line ${expanded || !isLong ? "" : "line-clamp-[8]"}`}
          data-testid={`research-topic-findings-${index}`}
        >
          {findings}
        </p>
      )}
      {topic.status === "done" && isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-[11px] text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 font-semibold transition-colors"
          data-testid={`button-toggle-research-findings-${index}`}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}

      {topic.status === "done" && sources.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-0.5" data-testid={`research-topic-sources-${index}`}>
          {sources.map((s, si) => (
            <ResearchSourceChip key={si} source={s} testId={`research-source-${index}-${si}`} />
          ))}
        </div>
      )}
    </div>
  );
}

function ResearchSection({ personaId, personaGenerating }: { personaId: string; personaGenerating: boolean }) {
  const { toast } = useToast();

  const { data: research } = useQuery<PersonaResearchView>({
    queryKey: ["/api/personas", personaId, "research"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!personaId,
    refetchInterval: (query) => {
      const d = query.state.data as PersonaResearchView | undefined | null;
      const active =
        d?.status === "generating" ||
        (d?.topics ?? []).some((t) => t.status === "pending" || t.status === "researching");
      // While the persona itself is still generating, research may not have
      // kicked off yet — keep polling so topics appear as they are decided.
      return active || personaGenerating ? 3000 : false;
    },
  });

  const retryMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/personas/${personaId}/research/retry`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/personas", personaId, "research"] });
      toast({ title: "Research restarted" });
    },
    onError: (e: Error) =>
      toast({ title: "Couldn't retry research", description: e.message, variant: "destructive" }),
  });

  const topics = research?.topics ?? [];
  // Personas without research (e.g. manually created ones) show nothing.
  if (!research || (!research.status && topics.length === 0)) return null;

  const doneCount = topics.filter((t) => t.status === "done").length;

  return (
    <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-5 persona-section-card" data-testid="card-research">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <SectionHeader
          icon={Microscope}
          title="Research"
          count={topics.length > 0 ? topics.length : undefined}
          accent="bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300"
        />
        {research.status === "generating" && (
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-4" data-testid="status-research-generating">
            <Loader2 className="h-3 w-3 animate-spin" />
            {topics.length > 0 ? `Researching ${doneCount}/${topics.length}` : "Researching…"}
          </span>
        )}
        {research.status === "failed" && (
          <Button
            size="sm"
            variant="outline"
            className="mb-4"
            onClick={() => retryMutation.mutate()}
            disabled={retryMutation.isPending}
            data-testid="button-retry-research"
          >
            {retryMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            )}
            Retry research
          </Button>
        )}
      </div>

      {research.status === "failed" && (
        <div
          className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/70 dark:bg-amber-950/20 px-3 py-2"
          data-testid="alert-research-failed"
        >
          <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <p className="text-[12px] text-amber-800 dark:text-amber-200 leading-relaxed">
            {research.error || "Persona research failed."}
            {" "}Retry re-runs the failed topics and keeps completed ones.
          </p>
        </div>
      )}

      {research.status === "generating" && topics.length === 0 && (
        <p className="text-[12px] text-muted-foreground italic" data-testid="text-research-deciding-topics">
          Deciding research topics…
        </p>
      )}

      <div className="space-y-3">
        {topics.map((t, i) => (
          <ResearchTopicCard key={t.id} topic={t} index={i} />
        ))}
      </div>
    </div>
  );
}

function ResearchSourceChip({ source, testId }: { source: string; testId: string }) {
  const domain = /^https?:\/\//i.test(source) ? sourceDomain(source) : null;
  if (!domain) {
    return (
      <span
        className="inline-flex items-center rounded-full border bg-muted/50 px-2 py-0.5 text-[10px] font-medium text-muted-foreground max-w-full break-all"
        data-testid={testId}
      >
        {source}
      </span>
    );
  }
  return (
    <a
      href={source}
      target="_blank"
      rel="noreferrer"
      title={source}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 rounded-full border bg-muted/40 hover:bg-muted px-2 py-0.5 transition-colors"
    >
      <img
        src={`https://www.google.com/s2/favicons?sz=64&domain=${domain}`}
        alt=""
        className="h-3.5 w-3.5 rounded-sm"
        loading="lazy"
      />
      <span className="text-[10px] font-medium text-foreground/70">{domain}</span>
    </a>
  );
}

function sourceDomain(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}
